import { Formik, useFormikContext } from "formik";
import { isPaidPaymentStatus } from "../../utils/paymentStatus";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import FieldHelpTooltip from "../../components/Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../../components/Help/HelpContent";
import PartsUsedSection from "./PartsUsedSection";
import StagedPartsSection from "./StagedPartsSection";
import ServiceSpecificsSection from "./ServiceSpecificsSection";
import ResourceField, { useActiveResources } from "./ResourceField";
import WorkflowPanel, { useBookingWorkflow } from "./workflow/WorkflowPanel";
import WorkflowRunPanel from "./workflow/WorkflowRunPanel";
import {
  buildWorkflowPayload,
  assignmentsSignature,
  formatStepAssignments,
  isServiceStepOptional
} from "./workflow/workflowAssignments";
import { describeConflict } from "../../services/resourceService";
import { getBookingWorkflow } from "../../services/workflowService";
import * as inventoryService from "../../services/inventoryService";
import { getInvoiceAssets } from "../../services/customerAssetService";
import * as estimationService from "../../services/estimationService";
import { buildInvoiceHtml, splitIntoOfferings } from "../../utils/invoiceHtml";
import {
  Accordion,
  Alert,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import * as apicall from "../../components/Apicall";
import { toast } from "react-toastify";
import * as invoiceService from "../../services/invoiceService";
import InvoiceCancelDialog from "../../components/Invoice/InvoiceCancelDialog";
import InvoiceDateDialog from "../../components/Invoice/InvoiceDateDialog";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import { FaRegCalendarAlt, FaInfoCircle } from "react-icons/fa";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { Dropdown } from "primereact/dropdown";
import UserContext from "../../components/context/UserContext";
import NewBookingCalendar from "../Dashboard/NewBookingCalendar";
import { getPersonaForRole, PERSONA } from "../../constants/roles";
import { google_places_key } from "../../constants/Constants";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { validMobileFormatByOwnPrefixTest } from "../../utils/phoneValidation";
import { fetchBookingStatusesForIndustry } from "../../utils/bookingStatus";
import { parseBookingRef } from "../../utils/bookingRef";
import { getBookingLabel } from "../../utils/industryLabels";
import { realProviderNames } from "../../utils/bookingAssignment";

// Backend sometimes stores missing values as the literal string "null"
// (e.g. orders synced in from Utilstop) instead of a real null/empty value —
// treat those the same as empty so they don't render as "null" or fail validation.
const cleanBackendValue = (v) =>
  v === null || v === undefined || String(v).trim().toLowerCase() === "null"
    ? ""
    : v;

// Human-readable names for the fields the validation schema guards, so a
// failed submit names the field the way the form labels it.
const FIELD_LABELS = {
  partner: "Profile",
  phoneNumber: "Mobile Number",
  userName: "Customer name",
  house_no: "Street / House no.",
  area: "Area",
  location: "Address",
  state: "State",
  country: "Country",
  email: "Email",
  lat: "Latitude",
  lng: "Longitude"
};

// Formik silently does nothing when validation fails on submit. Because most
// of this form lives inside collapsed accordions, an invalid field the user
// cannot see reads as "the Update button is broken". Surface the first error
// and scroll to the offending field instead.
const SubmitErrorReporter = () => {
  const { submitCount, errors, values } = useFormikContext();
  const reportedRef = useRef(0);

  useEffect(() => {
    if (submitCount === 0 || submitCount === reportedRef.current) return;

    const fields = Object.keys(errors || {});
    if (fields.length === 0) return;

    reportedRef.current = submitCount;

    const field = fields[0];
    const rawError = errors[field];
    const message =
      typeof rawError === "string"
        ? rawError
        : `${FIELD_LABELS[field] || field} is invalid`;

    toast.error(message);
    console.warn("Booking form validation failed:", errors);
    console.warn(
      "Booking form values at time of failed submit:",
      JSON.stringify(values)
    );

    const el = document.querySelector(`[name="${field}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus?.({ preventScroll: true });
    }
  }, [submitCount, errors]);

  return null;
};

// Invoice record shown on the booking page (from get_ezp_booking_by_id), or null if not invoiced.
const toInvoiceInfo = (b) =>
  b?.invoice_ref_id ? { ref: b.invoice_ref_id, number: b.invoice_number, url: b.invoice_url } : null;

function Bookings() {
  const routeLocation = useLocation();
  let service = JSON.parse(localStorage.getItem("services"));
  let vendor_id = JSON.parse(localStorage.getItem("userId"));
  const vendorProfile = JSON.parse(localStorage.getItem("vendorProfile")) || {};

  // const _ud = (() => {
  //   try {
  //     const d = localStorage.getItem("userDetails");
  //     return d && d !== "undefined" ? JSON.parse(d) : null;
  //   } catch {
  //     return null;
  //   }
  // })();

  const taxPercentage = parseFloat(vendorProfile?.tax || 0);
  // const [searchParams] = useSearchParams();
  // let group_id = searchParams.get("group_id");
  // The route carries the group id or the display booking id; an unreadable one
  // is stopped earlier, in BookingStepper/index.jsx.
  let { group_id: groupIdParam } = useParams();
  let group_id = parseBookingRef(groupIdParam).groupId || undefined;

  // Convert-to-booking entry point: "Convert to Booking" on an accepted
  // estimation navigates here as /vendor_bookings?from_estimation=<id> (a
  // query param, not a route param, since there's no group_id yet - this
  // is always a brand-new booking, never an edit). Deliberately does NOT
  // set group_id, so the submit handler below still takes the "new
  // booking → Checkout" path instead of the in-place update path.
  const fromEstimationId = new URLSearchParams(routeLocation.search).get(
    "from_estimation"
  );
  const [services, setServices] = useState(service);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  const [initialValues, setInitialValues] = useState({
    partner: 1,
    org_id: "",
    userName: "",
    phoneNumber: "",
    email: "",
    house_no: "",
    area: "",
    location: "",
    location_id: "",
    state: "",
    landmark: "",
    country: "",
    lat: "",
    lng: "",
    // time: "",
    status: 2, // In Progress — default service status for a new booking
    payment: 0
  });

  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(service?.[0] || {});
  const [initialServiceData, setInitialServiceData] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  // Latest serviceData for fetchServiceData's async continuation, which
  // otherwise only sees the render it was called from.
  const serviceDataRef = useRef(serviceData);
  serviceDataRef.current = serviceData;
  const [serviceTypes, setServiceTypes] = useState([]);
  const [activeType, setActiveType] = useState("null");
  const [groupedData, setGroupedData] = useState({});
  const sectionsRef = useRef({});
  const scrollContainerRef = useRef(null);
  const isScrollingRef = useRef(false);
  let isScroll = false;
  const [dateSelect, setDateSelect] = useState("");
  const [timeSelect, setTimeSelect] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [discount, setDiscount] = useState(0);
  const [priceSection, setPriceSection] = useState([]);
  // Edit-booking only: live parts subtotal lifted up from PartsUsedSection,
  // folded into getPrice()'s total. originalPartsSubtotalRef baselines it at
  // whatever value this session's first onPartsChanged call reports (i.e.
  // the booking's pre-existing parts, if any), so the balance-increase
  // banner below only reacts to parts added/removed during this edit
  // session, not parts the booking already had.
  const [editPartsSubtotal, setEditPartsSubtotal] = useState(0);
  const originalPartsSubtotalRef = useRef(null);
  const [showCalender, setShowCalender] = useState(false);
  const [providerSelected, setProviderSelected] = useState([]);
  // Optional bookable resource (bay, van, room, court, a driver ...). Kept
  // like providerSelected - outside Formik - and only ever sent to the API
  // when the field is on screen. A vendor with no resources sees no field and
  // sends no `resource_ids` key, so nothing about their booking changes.
  const { resources: activeResources } = useActiveResources();
  const [resourceIds, setResourceIds] = useState("");
  // Choosing a resource narrows the crew calendar to the people mapped to it; this shows everyone again.
  const [showAllCrew, setShowAllCrew] = useState(false);
  useEffect(() => {
    setShowAllCrew(false);
  }, [resourceIds]);
  const [resourceCurrentName, setResourceCurrentName] = useState("");
  // Booking workflow (Setup > Workflows): who does each step. Only present for a
  // service that has a workflow; for any other the panel is absent and no
  // `workflow` key is sent. Kept outside Formik like providerSelected.
  const {
    definition: workflowDef,
    initialAssignments: workflowInitial,
    stateKey: workflowStateKey,
    existing: workflowExisting
  } = useBookingWorkflow({ priceSection, groupId: group_id });
  // A saved booking that already has a workflow shows it in motion (Start / Done ... are
  // saved straight away); a booking without one gets the assign-on-save panel. Either way
  // WHO is on each step is part of this form and is saved by Update.
  const [runWorkflow, setRunWorkflow] = useState(null);
  useEffect(() => {
    setRunWorkflow(workflowExisting);
  }, [workflowExisting]);
  const formWorkflow = workflowDef || null;
  // The booking's own workflow is shown while the service still resolves to it; if the
  // service was changed to one with a different workflow (and nothing has started), the
  // panel shows the new one - what the server will switch to on save.
  const showRunWorkflow =
    Boolean(group_id) &&
    !!runWorkflow &&
    (!workflowDef || Number(workflowDef.template_id) === Number(runWorkflow.template_id));
  const [workflowAssign, setWorkflowAssign] = useState({});
  // What a "back from Checkout" navigation carried, so it survives the reload of the definition.
  const workflowRestoreRef = useRef(null);
  const workflowInitialSigRef = useRef("");
  useEffect(() => {
    const start = workflowRestoreRef.current || workflowInitial || {};
    workflowRestoreRef.current = null;
    setWorkflowAssign(start);
    workflowInitialSigRef.current = assignmentsSignature(workflowDef, workflowInitial || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowStateKey, workflowDef?.template_id]);
  const { id } = useParams();
  const [locationError, setLocationError] = useState("");
  const isEditing = Boolean(id);
  let navigate = useNavigate();

  // Crew persona: view + edit existing bookings only, no new booking
  // creation. This route serves both create (no group_id) and edit
  // (:group_id) from one component, so this single guard - rather than
  // hiding every "+ New Booking" button across the app - is what actually
  // makes the restriction correct even if an entry point is missed.
  useEffect(() => {
    if (getPersonaForRole(apicall.getRole()) === PERSONA.CREW && !group_id) {
      navigate("/access-denied");
    }
  }, [group_id]);
  const [timeRange, setTimeRange] = useState({ startTime: "", endTime: "" });
  const [customItems, setCustomItems] = useState([]);
  const { vendor, refreshVendorData } = useContext(UserContext);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [holidayCrews, setHolidayCrews] = useState();
  const [initialAppointments, setInitialAppointments] = useState([]);
  const [partner_id, setPartner_id] = useState(1);
  const [partners, setPartners] = useState([]);
  const [user_id, setUser_id] = useState(0);
  const [userFlexiSubs, setUserFlexiSubs] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [existingSubscriptionId, setExistingSubscriptionId] = useState(null);
  const flagCount = useRef(0);
  const [bookingStatus, setBookingStatus] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [firstLocation, setFirstLocation] = useState(null);
  const [existingData, setExistingData] = useState([]);
  // Invoice record of the booking being edited ({ ref, number, url }) or null. Comes with
  // get_ezp_booking_by_id; refreshed after generating / cancelling without reloading the form.
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [showCancelInvoice, setShowCancelInvoice] = useState(false);
  // Generate Invoice popup (asks for the invoice date).
  const [showInvoiceDate, setShowInvoiceDate] = useState(false);
  useEffect(() => {
    setInvoiceInfo(toInvoiceInfo(existingData?.[0]));
  }, [existingData]);
  const [open, setOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [maxTime, setMaxTime] = useState(30);
  const [showOrderConf, setShowOrderConf] = useState(false);
  const [modelData, setModelData] = useState({});
  const [display_booking_id, setDisplay_booking_id] = useState("");
  const [editable, setEditable] = useState(true);
  const [currency, setCurrency] = useState(
    vendorProfile?.currency || localStorage.getItem("currency") || "AED"
  );
  const [userDetails, setUserDetails] = useState(null);
  const [calenderMessage, setCalenderMessage] = useState("");
  const [paidAmount, setPaidAmount] = useState(0);
  const latestRequestRef = useRef(0);
  const [industry, setIndustry] = useState([]);

  const isSportsService = industry?.industry === 7;
  const isCarService = industry?.industry === 8;
  const isSalonService = industry?.industry === 9;

  // Lifted from ServiceSpecificsSection (the real, industry-configurable
  // replacement for the old dead Car Details demo fields this used to be).
  // Persisted via a separate ezp_booking_service_details upsert, not part
  // of the main booking payload - see handleSubmit (edit) / Checkout.jsx (new).
  const [serviceSpecifics, setServiceSpecifics] = useState({ customer_asset_id: null, customer_complaint: "", action_taken: "" });

  // Guards fetchServiceData against the edit-mode race: selectedService starts
  // out seeded from localStorage (stale, from a previous session/booking), so
  // the [selectedService] effect fires fetchServiceData once immediately on
  // mount with the wrong service/partner/existingData, before getDataByID has
  // loaded the real booking and corrected selectedService/partner_id. Both
  // calls' responses land unordered — without this guard, whichever resolves
  // last wins and can silently overwrite the correct Service Category/Sub
  // Category/Service Type selection with the stale call's data.
  const latestServiceFetchRef = useRef(0);
  // What fetchServiceData last ran against — see the carry-over note there.
  const lastServiceFetchKeyRef = useRef({ existingData: null, partner: null });
  const pendingSubRef = useRef(null);
  const customerSelectedRef = useRef(false);
  const latestUserLookupRef = useRef(0);
  const checkoutReturnServiceDataRef = useRef(null);
  const originalSnapshotRef = useRef(null);
  // Parts Used (Feature 4) saves itself instantly via its own API calls, so
  // it never shows up in the "unchanged" diff below - without this flag, a
  // vendor who only added/removed parts and then hits Update sees the
  // confusing "No changes detected" warning even though they did change
  // something (it just didn't need this button to persist).
  const partsChangedRef = useRef(false);
  // Service Specifics lives in its own state/table too, so it needs its own
  // baseline for the "unchanged" diff - otherwise editing only the asset,
  // complaint or action-taken note reads as "No changes detected" and the
  // save below it never runs. Null until ServiceSpecificsSection reports
  // what's saved (treated as empty, see getSpecificsBaseline).
  const originalServiceSpecificsRef = useRef(null);
  const normalizeSpecifics = (s) => ({
    asset: String(s?.customer_asset_id ?? ""),
    complaint: s?.customer_complaint || "",
    action: s?.action_taken || "",
  });
  const handleServiceSpecificsLoaded = useCallback((saved) => {
    originalServiceSpecificsRef.current = normalizeSpecifics(saved);
  }, []);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [vendorBlockedDates, setVendorBlockedDates] = useState([]);
  const resetBookingForm = () => {
    setInitialValues({
      partner: partner_id || 1,
      org_id: "",
      userName: "",
      phoneNumber: "",
      email: "",
      house_no: "",
      area: "",
      location: "",
      location_id: "",
      state: "",
      landmark: "",
      country: vendorProfile?.currency === "INR" ? 2 : 1,
      lat: "",
      lng: "",
      status: 2, // In Progress — default service status for a new booking
      payment: ""
    });

    let today = new Date();
    let d1 = today.toLocaleDateString("en-GB").split(/[\/\-\.]/);
    let d2 = `${d1[2]}-${d1[1]}-${d1[0]}`;
    setDateSelect(d2);
    setInitialServiceData([]);
    setServiceTypes([]);
    setGroupedData({});
    setActiveType("null");

    // setProviderSelected([]);
    setFilteredAppointments([]);
    setInitialAppointments([]);

    setTimeSelect("");
    setAdditionalNotes("");
    setDiscount(0);
    setPriceSection([]);

    setExistingData([]);
    setDisplay_booking_id("");
    setEditable(true);
    setUser_id(0);
    setResourceIds("");
    setResourceCurrentName("");
    customerSelectedRef.current = false;
  };
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apicall.get("ezpMyProfile");
        const profile = response?.data?.[0];
        if (profile) {
          setCurrency(profile.currency || "AED");
          setUserDetails(profile);
          // industry (drives isCarService/isSportsService below, which
          // decide whether address fields are required) must come from the
          // fresh ezpMyProfile fetch, not localStorage — this was fetched
          // here already for currency, so reuse it instead of a second,
          // never-actually-called fetchVendorProfile request.
          setIndustry(profile);
          if (profile.id) fetchDoctorHolidays(profile.id);
        }
      } catch (e) {
        console.error("Failed to fetch profile for currency:", e);
      }
    };
    fetchProfile();
  }, []);
  const fetchVendorProfile = async () => {
    try {
      const [response] = await Promise.all([apicall.get("ezpMyProfile")]);

      if (response.status === 200 && response.success) {
        const vendorData = response.data[0];
        setIndustry(vendorData);
      }
    } catch (error) {
      console.error("Error fetching vendor profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!group_id) {
      // resetBookingForm();
      getAllServices();
    }
  }, [group_id]);
  const providerIds = useMemo(() => {
    const ids = new Set();

    initialAppointments?.forEach((item) => {
      if (item?.extendedProps?.provider_id) {
        ids.add(item.extendedProps.provider_id);
      }
    });

    return [...ids];
  }, [initialAppointments]);

  const excludedDates = unavailableDates
    .map((item) => new Date(item.date))
    .filter((d) => !isNaN(d));

  // console.log(excludedDates,"excludedDates")
  // Validation Schema
  const validationSchema = Yup.object().shape({
    partner: Yup.string().required("Profile is required"),
    phoneNumber: Yup.string()
      .required("Mobile Number is required")
      // .matches(/^[0-9]+$/, "Phone Number must be numeric")
      .test("country-specific-length", function (value) {
        if (!value) return true;
        const cleanedValue = value.replace(/\D/g, "");
        if (cleanedValue.startsWith("91")) {
          return (
            cleanedValue.length >= 12 ||
            this.createError({
              message:
                "Please enter a valid Indian mobile number (min 10 digits)"
            })
          );
        }
        if (cleanedValue.startsWith("971")) {
          return (
            cleanedValue.length >= 12 ||
            this.createError({
              message: "Please enter a valid UAE mobile number (min 9 digits)"
            })
          );
        }
        // Default: minimum 10 digits (including country code)
        return (
          cleanedValue.length >= 10 ||
          this.createError({
            message: "Please enter a valid mobile number (min 10 digits)"
          })
        );
      })
      .test("country-match-profile", function (value) {
        if (!value) return true;

        // Utilstop profile bookings can use a number from any country —
        // only enforce the profile-country match for non-Utilstop profiles.
        const selectedPartnerId = Number(this.parent?.partner);
        const selectedPartner = partners?.find(
          (p) => Number(p.id) === selectedPartnerId
        );
        const isUtilstopProfile =
          selectedPartner?.partner_name?.trim().toLowerCase() === "utilstop";
        if (isUtilstopProfile) return true;

        const cleanedValue = value.replace(/\D/g, "");
        const wrongPrefix = currency === "INR" ? "971" : "91";
        const expectedCountry = currency === "INR" ? "India" : "UAE";
        if (cleanedValue.startsWith(wrongPrefix)) {
          return this.createError({
            message: `Please select a ${expectedCountry} number — this matches your profile's country.`
          });
        }
        return true;
      })
      .test("valid-mobile-format", validMobileFormatByOwnPrefixTest()),
    userName: Yup.string()
      .required(isSportsService ? "Player name is required" : "Customer name is required")
      .min(2, isSportsService ? "Player name must be at least 2 characters" : "Customer name must be at least 2 characters")
      .max(20, isSportsService ? "Player name must not exceed 20 characters" : "Customer name must not exceed 20 characters"),

    house_no:
      isSportsService || isCarService || isSalonService
        ? Yup.string().max(100, "Street address must not exceed 100 characters")
        : Yup.string()
            .required("Street / House no. is required")
            // .min(5, "Street address must be at least 5 characters")
            .max(100, "Street address must not exceed 100 characters"),

    area:
      isSportsService || isCarService || isSalonService
        ? Yup.string()
        : Yup.string().required("Area is required"),
    location:
      isSportsService || isCarService || isSalonService
        ? Yup.string()
        : Yup.string().required("Address is required"),
    state:
      isSportsService || isCarService || isSalonService
        ? Yup.string()
        : Yup.string().when("country", {
            is: (country) => {
              const selected = countries.find(
                (c) => Number(c.country_id) === Number(country)
              );

              return selected?.name?.toLowerCase().includes("india");
            },
            then: (schema) => schema.required("State is required"),
            otherwise: (schema) => schema.required("Emirate is required")
          }),
    country:
      isSportsService || isCarService ||  isSalonService
        ? Yup.string()
        : Yup.string().required("Country is required"),
    // time: Yup.string().required("Time is required"),

    lat: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-latitude", "Invalid latitude value", (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue >= -90 && numValue <= 90;
      }),

    lng: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-longitude", "Invalid longitude value", (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue >= -180 && numValue <= 180;
      }),
    email: Yup.string()
      .nullable()
      .test(
        "valid-email",
        "Enter valid email (.com, .in, .co.in only)",
        (value) => {
          if (!value) return true;

          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|co\.in)$/;

          return emailRegex.test(value);
        }
      )
    // status: Yup.string().required("Status is required"),
    // payment: Yup.string().required("Payment is required"),
  });

  useEffect(() => {
    getAllServices();
  }, [partner_id]);

  // Restore form when user navigates back from Checkout via "Add Item"
  useEffect(() => {
    const state = routeLocation.state;
    if (!state?.checkoutReturn || !state?.bookingValues) return;
    const bv = state.bookingValues;
    setInitialValues({
      partner: bv.partner || 1,
      org_id: bv.org_id || "",
      userName: bv.userName || "",
      phoneNumber: bv.phoneNumber || "",
      email: bv.email || "",
      house_no: bv.house_no || "",
      area: bv.area || "",
      location: bv.location || "",
      location_id: bv.location_id || "",
      state: bv.state || "",
      landmark: bv.landmark || "",
      country: bv.country || 1,
      lat: bv.lat || "",
      lng: bv.lng || "",
      status: bv.status || "",
      payment: bv.payment || ""
    });
    if (bv.date) setDateSelect(bv.date);
    if (bv.time) setTimeSelect(bv.time);
    if (bv.priceSection) setPriceSection(bv.priceSection);
    if (bv.additional_notes !== undefined)
      setAdditionalNotes(bv.additional_notes);
    if (bv.paid_amount !== undefined) setPaidAmount(bv.paid_amount);
    if (state.partner_id) setPartner_id(state.partner_id);
    // Restore service/type/offering selection — fetchServiceData will use this ref
    if (bv.serviceData?.length > 0) {
      checkoutReturnServiceDataRef.current = bv.serviceData;
      const addedItem = bv.serviceData.find((s) => s.added);
      if (addedItem?.service_type_name)
        setActiveType(addedItem.service_type_name);
    }
    // Restore crew/provider selection so form validation passes
    // Restore the optional resource too, so what was picked before "Add Item" survives.
    if (bv.resource_ids !== undefined) {
      setResourceIds(String(bv.resource_ids || ""));
    }
    if (bv.workflow?.steps?.length) {
      workflowRestoreRef.current = Object.fromEntries(
        bv.workflow.steps
          .filter((x) => x.user_id)
          .map((x) => [x.step_key, Number(x.user_id)])
      );
    }
    if (bv.providerSelectedData?.length > 0) {
      setProviderSelected(
        bv.providerSelectedData.map((s) => ({
          extendedProps: { provider_id: s.provider_id },
          start: s.start,
          end: s.end,
          title: s.title
        }))
      );
    }
    customerSelectedRef.current = true;
    // Clear navigation state so refreshing doesn't re-apply
    window.history.replaceState({}, "");
  }, [routeLocation.state]);

  // Show order confirmation modal when navigated back from Checkout after a new booking
  useEffect(() => {
    const state = routeLocation.state;
    if (state?.showOrderConf && state?.confData) {
      setModelData(state.confData);
      setShowOrderConf(true);
      window.history.replaceState({}, "");
    }
  }, [routeLocation.state]);

  useEffect(() => {
    fetchServiceData(selectedService);
  }, [selectedService]);

  useEffect(() => {
    // Skip only when restoring from Checkout's Back button - this effect
    // only ever fires once, on mount, and that's exactly when the
    // checkoutReturn restore effect above just set the real saved date.
    if (!routeLocation.state?.checkoutReturn) {
      let today = new Date();
      let d1 = today.toLocaleDateString("en-GB").split(/[\/\-\.]/);
      let d2 = `${d1[2]}-${d1[1]}-${d1[0]}`;
      setDateSelect(d2);
    }
    // getAllServices();
    fetchPartners();
  }, [group_id]);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;
      if (isScroll) return;

      // console.log("scroll------");
      const scrollPos = scrollEl.scrollTop; // adjust offset

      for (const type of serviceTypes) {
        const section = sectionsRef.current[type];
        if (section) {
          const { offsetTop, offsetHeight } = section;
          if (scrollPos >= offsetTop && scrollPos < offsetTop + offsetHeight) {
            setActiveType(type);
            break;
          }
        }
      }
    };
    scrollEl.addEventListener("scroll", handleScroll);
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, [serviceTypes]);

  const formatPhoneNumber = (phone, currency) => {
    if (!phone) return "";

    let cleaned = phone.toString().replace(/\D/g, "");

    if (currency === "INR") {
      // Remove India country code if already present
      if (cleaned.startsWith("91") && cleaned.length > 10) {
        cleaned = cleaned.slice(2);
      }

      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }

    if (currency === "AED") {
      // Remove UAE country code if already present
      if (cleaned.startsWith("971") && cleaned.length > 9) {
        cleaned = cleaned.slice(3);
      }

      return `+971 ${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
    }

    return phone;
  };

  const autocompleteRef = useRef(null);

  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: google_places_key,
    libraries: ["places"]
  });
  const onPlaceChanged = (setFieldValue) => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) return;

    let street = place.formatted_address || "";
    let area = "";
    let state = "";
    let country = "";
    let lat = place.geometry?.location?.lat();
    let lng = place.geometry?.location?.lng();

    place.address_components.forEach((comp) => {
      const types = comp.types;

      if (types.includes("sublocality") || types.includes("locality")) {
        area = comp.long_name;
      }

      if (types.includes("administrative_area_level_1")) {
        state = comp.long_name;
      }

      if (types.includes("country")) {
        country = comp.short_name?.toLowerCase(); //  "in" / "ae"
      }
    });

    //  expected country based on currency
    const expectedCountry = currency === "INR" ? "in" : "ae";

    // console.log("Selected:", country, "Expected:", expectedCountry);

    // VALIDATION
    if (!country || country !== expectedCountry) {
      setLocationError(
        currency === "INR"
          ? "Please select a location within India"
          : "Please select a location within UAE"
      );

      setFieldValue("location", "");
      setFieldValue("area", "");
      setFieldValue("state", "");
      setFieldValue("lat", "");
      setFieldValue("lng", "");

      return;
    }

    //  CLEAR ERROR
    setLocationError("");

    //  SET VALUES
    setFieldValue("location", street);
    setFieldValue("area", area);
    setFieldValue("state", state);
    setFieldValue("country", country);
    setFieldValue("lat", lat);
    setFieldValue("lng", lng);
  };
  useEffect(() => {
    arrangeData(true);
  }, [initialServiceData]);

  // Skip only the very first (mount) firing so a checkout-return's restored
  // crew selection survives it - selectedService can genuinely change again
  // later (vendor picks a different Service Category), and clearing crew
  // then is still correct, so this must not be gated by checkoutReturn for
  // the whole session, only for this one initial run.
  const skipInitialProviderClearRef = useRef(true);
  useEffect(() => {
    if (skipInitialProviderClearRef.current) {
      skipInitialProviderClearRef.current = false;
      return;
    }
    if (!group_id) {
      setProviderSelected([]);
    }
  }, [selectedService]);

  useEffect(() => {
    if (providerSelected?.length > 0) {
      setTimeSelect(providerSelected[0]?.start?.split("T")?.[1]);
    }
  }, [providerSelected]);

  useEffect(() => {
    arrangeData();
    getPrice();
  }, [serviceData]);

  // Edit booking: editPartsSubtotal changing (parts added/removed via
  // PartsUsedSection) doesn't touch serviceData, so it needs its own trigger
  // to fold into the price summary/Balance Due.
  useEffect(() => {
    getPrice();
  }, [editPartsSubtotal]);

  const getStatusColors = (status) => {
    // Define color schemes for different statuses with requested light colors
    const statusColors = {
      //  Green - completed
      1: {
        bgColor: "#F2FFF0",
        // border: "1px solid #C6E4C6",

        // bgColor: "#fff",
        // border: "1px solid #C6E4C6",
        textColor: "#666600"
      },
      //Selected - Light blue
      2: {
        bgColor: "#FFF0FD",
        borderColor: "#92007E",
        textColor: "#92007E"
      },
      // Cancelled / red - Not avaiable
      3: {
        bgColor: "#FDEDED",
        borderColor: "#FF0000",
        textColor: "#FF0000"
      },
      4: {
        bgColor: "#28a745",
        borderColor: "#FF0000",
        textColor: "#FF0000"
      },
      // Break
      6: {
        bgColor: "#FDEDED",
        borderColor: "#FF0000",
        textColor: "#FF0000"
      },

      10: {
        bgColor: "#FDEDED",
        borderColor: "#FF0000",
        textColor: "#FF0000"
      },
      // Checked In (11) - Light blue
      11: {
        bgColor: "#FFF0FD",
        borderColor: "#92007E",
        textColor: "#92007E"
      },
      // Cancelled (14) - Light red
      14: {
        bgColor: "rgba(255, 204, 204, 0.5)",
        borderColor: "#CC0000",
        textColor: "#990000"
      },
      // No Show (15) - Light orange
      12: {
        bgColor: "F2F9FF",
        borderColor: "#037FE8",
        textColor: "#037FE8"
      },
      // Completed/Checked Out (15) - Light green
      15: {
        bgColor: "rgba(255, 255, 204, 0.5)",
        borderColor: "#CCCC00",
        textColor: "#666600"
      },
      99: {
        bgColor: "#FFE5E5",
        borderColor: "#FF4D4D",
        textColor: "#B30000"
      },
      // Default - Light gray
      default: {
        bgColor: "rgba(230, 230, 230, 0.5)",
        borderColor: "#999999",
        textColor: "#666666"
      }
    };

    return statusColors[status] || statusColors.default;
  };

  // The chosen resource's pool, as the people who can do the service (professionals;
  // drivers are not in this grid). With no such person mapped nothing is filtered,
  // and a slot that is already selected always stays visible.
  const chosenResource = activeResources.find((r) => String(r.id) === String(resourceIds));
  const poolProfessionalIds = (chosenResource?.crew || [])
    .filter((c) => (c.skill_key || "PROFESSIONAL") === "PROFESSIONAL")
    .map((c) => Number(c.user_id));
  const poolFilterActive = poolProfessionalIds.length > 0 && !showAllCrew;

  const appointments = filteredAppointments
    ?.filter(
      (item) =>
        !poolFilterActive ||
        item?.status === 2 ||
        poolProfessionalIds.includes(Number(item?.extendedProps?.provider_id))
    )
    .map((item) => {
    const colors = getStatusColors(item?.status);

    return {
      title: `${item.title?.trim()}`,
      search: item.title?.trim(),
      resourceId: item.title?.trim(),
      start: item?.start,
      end: item?.end,
      backgroundColor: colors.bgColor,
      borderColor: colors.borderColor,
      textColor: colors.textColor,
      extendedProps: {
        ...item
      },
      // Optional: Add event class for additional styling
      className: `status-${item?.status || "default"}`
    };
  });

  const fetchDoctorHolidays = async (userId) => {
    try {
      const response = await apicall.get(
        `get_vendor_holidays?user_id=${userId}`
      );

      const holidays = response.data || [];

      const blocked = holidays.map((item) => {
        const d = new Date(item.date);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      });

      setVendorBlockedDates(blocked);
    } catch (error) {
      console.error("Error fetching holidays:", error);
      toast.error("Failed to fetch holidays");
    }
  };

  async function getAllServices() {
    const requestId = ++latestRequestRef.current;

    try {
      setLoading(true);

      const response = await apicall.get(
        `get_ezp_service?partner_id=${partner_id}`
      );

      // ❗ Ignore if not latest call
      if (requestId !== latestRequestRef.current) return;

      if (response.success && response.data) {
        setServices(response.data);

        if (group_id) {
          getDataByID(response.data);
        } else if (fromEstimationId && !customerSelectedRef.current) {
          loadFromEstimation(response.data, fromEstimationId);
        } else if (!customerSelectedRef.current) {
          setSelectedService(response.data?.[0]);
          fetchServiceData(response.data?.[0]);
          resetBookingForm();

          setInitialValues({
            partner: partner_id,
            org_id: "",
            userName: "",
            phoneNumber: "",
            email: "",
            house_no: "",
            area: "",
            location: "",
            location_id: "",
            state: "",
            landmark: "",
            country: vendorProfile?.currency === "INR" ? 2 : 1,
            lat: "",
            lng: "",
            status: 2, // In Progress — default service status for a new booking
            payment: ""
          });
        }
      }
    } catch (e) {
      console.log("e----", e);
      setServices([]);
    } finally {
      // Only stop loading for latest request
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (initialValues.phoneNumber) {
      getRecentBookings(initialValues.phoneNumber);
    } else {
      setRecentBookings([]);
    }
  }, [initialValues.phoneNumber]);
  async function getDataByID(allServicesData) {
    try {
      setLoading(true);
      const response = await apicall.get(
        `get_ezp_booking_by_id?group_id=${group_id}`
      );
      console.log(response, "response---");
      setLoading(false);
      if (response.success && response.data && response.data.length > 0) {
        setExistingData(response?.data || []);
        let {
          customer_id,
          partner,
          org_id,
          user_name,
          first_name,
          middle_name,
          last_name,
          email,
          phone_number,
          house_no,
          area,
          state,
          location,
          location_id,
          landmark,
          status,
          payment,
          lat,
          lng,
          service_id,
          service_date,
          service_time,
          discount,
          display_booking_id,
          editable,
          additional_info,
          total_paid_amount,
          service_type_name,
          subscription_id
        } = response.data[0];
        // Backend concatenates first_name + last_name with SQL CONCAT, which
        // returns NULL entirely when last_name is null (e.g. customers synced
        // from another system with no last_name on record). Rebuild from the
        // individual name parts so a missing last_name doesn't blank out the name.
        const resolvedUserName =
          user_name ||
          `${first_name || ""} ${middle_name || ""} ${last_name || ""}`
            .replace(/\s+/g, " ")
            .trim();
        let d1 = {
          partner: partner,
          org_id: org_id,
          userName: cleanBackendValue(resolvedUserName),
          phoneNumber: cleanBackendValue(phone_number),
          email: cleanBackendValue(email),
          house_no: cleanBackendValue(house_no),
          area: cleanBackendValue(area),
          location: cleanBackendValue(location),
          location_id: location_id,
          state: cleanBackendValue(state),
          landmark: cleanBackendValue(landmark),
          country: vendorProfile?.currency === "INR" ? 2 : 1,
          lat: lat,
          lng: lng,
          status: status,
          payment: payment
        };
        setInitialValues(d1);

        setDateSelect(service_date?.split("T")?.[0] || service_date);
        setTimeSelect(service_time);
        setDiscount(discount);
        setAdditionalNotes(additional_info);
        setPaidAmount(total_paid_amount);
        setExistingSubscriptionId(subscription_id || null);
        // Parse custom services from backend
        const customString = response.data[0]?.custom_items;
        // if (phone_number) {
        //   getRecentBookings(phone_number);
        // }
        if (customString) {
          const customArray = customString.split("||").map((item) => {
            const [name, amount] = item.split("::");

            return {
              id: Date.now() + Math.random(),
              name: name,
              amount: amount,
              isCustom: true
            };
          });

          setPriceSection(customArray);
        }
        setPartner_id(partner);
        setDisplay_booking_id(display_booking_id);
        setEditable(editable);
        setUser_id(customer_id);
        setActiveType(service_type_name || "null");

        // Crew: response.data has one row per service_offering in this booking
        // group, and each row's provider column holds ALL crew for that
        // offering as a comma-joined string (e.g. "197,237"). Split every row's
        // value and merge/dedupe across rows so crew show up correctly whether
        // there's one offering or several.
        let providers = [
          ...new Set(
            response.data
              .flatMap((r) => String(r.provider || "").split(","))
              .map((s) => Number(s.trim()))
              .filter((n) => Number.isFinite(n) && n > 0)
          )
        ];

        // Service Category must be matched against the service list for THIS
        // booking's real partner. allServicesData (passed in from getAllServices)
        // may still be the list fetched for the previous/default partner_id — from
        // before this booking's actual partner was known — which can be empty or
        // simply not include this service. Rather than depend on setPartner_id(...)
        // below indirectly retriggering a fresh getAllServices()/getDataByID() pass
        // (a fragile, easy-to-break cascade), fetch the authoritative list for the
        // confirmed-correct partner right here and match against that directly.
        let matchedService;
        try {
          const svcResponse = await apicall.get(
            `get_ezp_service?partner_id=${partner}`
          );
          if (svcResponse.success && svcResponse.data) {
            setServices(svcResponse.data);
            allServicesData = svcResponse.data;
          }
        } catch (e) {
          console.log("err fetching services for booking's partner----", e);
        }
        matchedService = allServicesData?.find((i) => i.id === service_id);

        let selectedId;
        if (matchedService) {
          selectedId = service_id;
          setSelectedService(matchedService);
        }
        let q1 = `getWokerAvailableSlots?date=${service_date}`;
        if (selectedId) {
          q1 += `&&service_id=${selectedId}`;
        }
        // setLoading(true);
        let getWorkerAvailable = await apicall.get(q1);

        let updatedData = getWorkerAvailable?.data || [];

        setInitialAppointments(getWorkerAvailable?.data || []);
        let dt = `${service_date}T${service_time}:00`;
        // console.log("dt---", dt);

        let p1 = [];
        for (let i of providers) {
          for (let j of updatedData) {
            if (i === j?.extendedProps?.provider_id && dt === j.start) {
              j.status = 2;
              p1.push(j);
            }
          }
        }
        // console.log("p1---", p1);
        setProviderSelected(p1);

        // Save original snapshot for change detection
        partsChangedRef.current = false;
        // Service Specifics baseline is re-reported by the child once its
        // own fetch for this booking resolves.
        originalServiceSpecificsRef.current = null;
        // Re-baseline the parts-subtotal tracking too, in case this
        // component instance is being reused for a different booking rather
        // than freshly mounted (client-side route change between two edit
        // URLs) - otherwise the previous booking's baseline/subtotal would
        // leak into this one's balance-increase calculation.
        originalPartsSubtotalRef.current = null;
        setEditPartsSubtotal(0);
        // Establish the parts-subtotal baseline from a dedicated, independent
        // fetch rather than from PartsUsedSection's own onPartsChanged calls -
        // that component's first effect fires with subtotal=0 before its own
        // getBookingParts resolves, so "whichever call arrives first" is not
        // a safe way to know the booking's real starting subtotal (it either
        // wrongly baselines at 0 for a booking that already has parts, or a
        // later reset here can land after a real add and erase it). This
        // fetch runs in parallel with the rest of this function and with
        // PartsUsedSection's own fetch - a small duplication, but it removes
        // the race entirely rather than just narrowing it.
        inventoryService
          .getBookingParts(response.data[0].id)
          .then((res) => {
            const initialPartsSubtotal = (Array.isArray(res?.data) ? res.data : []).reduce(
              (sum, p) => sum + Number(p.quantity) * Number(p.unit_price),
              0
            );
            originalPartsSubtotalRef.current = initialPartsSubtotal;
            setEditPartsSubtotal(initialPartsSubtotal);
          })
          .catch(() => {
            originalPartsSubtotalRef.current = null;
          });
        originalSnapshotRef.current = {
          user_name: user_name || "",
          phone_number: String(phone_number || ""),
          email: email || "",
          house_no: house_no || "",
          area: area || "",
          location: location || "",
          state: state || "",
          landmark: landmark || "",
          lat: String(lat || ""),
          lng: String(lng || ""),
          service_date: service_date?.split("T")?.[0] || service_date || "",
          service_time: (service_time || "").slice(0, 5),
          additional_info: additional_info || "",
          discount: Number(discount || 0),
          total_paid_amount: Number(total_paid_amount || 0),
          status: Number(status || 0),
          payment: Number(payment || 0),
          offering_ids: response.data
            .map((d) => d.service_offering_id)
            .sort()
            .join(","),
          providers: p1
            .map((p) => `${p.extendedProps?.provider_id}:${p.start}`)
            .sort()
            .join(","),
          resource_ids: String(response.data[0]?.resource_ids || "")
        };
        setResourceIds(String(response.data[0]?.resource_ids || ""));
        setResourceCurrentName(String(response.data[0]?.resource_names || ""));

        // if (!editable) {
        //   let ans = updatedData.filter((i) => {
        //     let hours = timeSelect.split(":")[0];
        //     let mins = timeSelect.split(":")[1];

        //     let res = i.start.split("T")?.[1];
        //     // console.log(
        //     //   "res--",
        //     //   res,
        //     //   `${hours}:${mins}:00`,
        //     //   res === `${hours}:${mins}:00`
        //     // );
        //     return res === `${hours}:${mins}:00`;
        //     //   if (res === `${hours}:${mins}:00`) {
        //     //   i.start = i.start.split("T")[0] + "T" + `${hours}:${mins}:00`;
        //     // }
        //   });
        //   updatedData = ans;
        // }

        // Use p1.length (local, already populated) instead of crewCount
        // (crewCount is a stale closure value = 0 at this point because
        //  setProviderSelected(p1) hasn't caused a re-render yet)
        const loadedCrewCount = p1.length || 1;
        for (let i of updatedData) {
          if (i.status === 2) {
            let date = new Date(i?.start);
            let t1 = maxTime > 0 ? maxTime / loadedCrewCount : 60; // fallback 60 min
            date.setMinutes(date.getMinutes() + t1);
            i.end = convertWithoutTimseStamp(date);
          }
        }
        let u1 = cleanCrewSlots(updatedData);

        const result = u1.reduce(
          (acc, item) => {
            return {
              minStart: item.start < acc.minStart ? item.start : acc.minStart,
              maxEnd: item.end > acc.maxEnd ? item.end : acc.maxEnd
            };
          },
          {
            minStart: u1[0].start,
            maxEnd: u1[0].end
          }
        );
        let min1 = result?.minStart?.split?.("T")?.[1];
        let max1 = result?.maxEnd?.split?.("T")?.[1];
        if (!min1 || min1.includes("NaN")) min1 = "08:00:00";
        if (!max1 || max1.includes("NaN")) max1 = "20:00:00";
        setTimeRange({
          startTime: min1,
          endTime: max1
        });

        setFilteredAppointments(u1 || []);
      } else {
      }
    } catch (e) {
      console.log("e----", e);
    } finally {
      setLoading(false);
    }
  }

  // Convert-to-booking pre-fill: reuses the exact same setters getDataByID
  // uses for the edit-booking load path (setInitialValues/setExistingData/
  // setSelectedService), just built from an estimation's fields instead of
  // a booking row. Deliberately does NOT call fetchServiceData directly -
  // setting selectedService/existingData here and letting the existing
  // `useEffect(() => fetchServiceData(selectedService), [selectedService])`
  // (below) fire is what getDataByID itself relies on, so this stays
  // consistent with that timing (existingData must land before
  // fetchServiceData's "mark added" matching runs against it).
  async function loadFromEstimation(allServicesData, estimationId) {
    try {
      setLoading(true);
      const response = await estimationService.getEstimation(estimationId);
      if (!response.success || !response.data) {
        toast.error(response.message || "Quotation not found");
        return;
      }
      const e = response.data;

      setInitialValues({
        partner: partner_id,
        org_id: "",
        userName: cleanBackendValue(e.customer_name),
        phoneNumber: cleanBackendValue(e.phone_number),
        email: cleanBackendValue(e.email),
        house_no: cleanBackendValue(e.house_no),
        area: cleanBackendValue(e.area),
        location: cleanBackendValue(e.location),
        location_id: "",
        state: cleanBackendValue(e.state),
        landmark: cleanBackendValue(e.landmark),
        country: e.country || (vendorProfile?.currency === "INR" ? 2 : 1),
        lat: e.lat || "",
        lng: e.lng || "",
        status: 2, // In Progress — default service status for a new booking
        payment: ""
      });
      customerSelectedRef.current = true;

      setDiscount(Number(e.discount_value) || 0);
      setAdditionalNotes(e.notes || "");

      // Synthesize the same shape getDataByID populates existingData with
      // (service_offering_id + price) so fetchServiceData's unmodified
      // "mark added" matching logic works against an estimation's
      // offerings exactly as it does against a booking's.
      setExistingData(
        (e.offerings || []).map((o) => ({
          service_offering_id: o.service_offering_id,
          price: o.unit_price
        }))
      );
      const matchedService = allServicesData?.find(
        (s) =>
          String(s.id) === String(e.service_id) ||
          String(s.service_id) === String(e.service_id)
      );
      if (matchedService) setSelectedService(matchedService);

      // Seed staged inventory parts (StagedPartsSection) from the
      // estimation's items - same row shape addStagedPart builds.
      const partRows = (e.items || []).map((i) => ({
        name: i.item_name,
        amount: Number(i.quantity) * Number(i.unit_price),
        isPart: true,
        item_id: i.item_id,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        unit_of_measure: i.unit_of_measure,
        id: `part-${i.item_id}-${Date.now()}`
      }));
      setPriceSection(partRows);
      getPrice(partRows);
    } catch (err) {
      console.log("err loading estimation----", err);
      toast.error("Failed to load quotation");
    } finally {
      setLoading(false);
    }
  }

  function getPrice(basisPriceSection) {
    try {
      // Accepting an explicit basis (rather than always reading the
      // `priceSection` closure) matters when a caller just spliced a new row
      // (e.g. addStagedPart/removeStagedPart) and calls getPrice(copy) in the
      // same tick: setPriceSection(copy) and this function's own
      // setPriceSection(prices) call batch together, so without an explicit
      // basis this would rebuild from the pre-splice array and silently
      // discard the just-made change (the same pre-existing bug
      // deleteCustomItem works around by not calling this function at all).
      const basis = basisPriceSection || priceSection;
      let prices = [];
      let total = 0;

      // Existing services total
      for (let eachData of serviceData) {
        if (eachData?.added) {
          let name = eachData.service_name + "- ";

          if (
            eachData.service_type_name &&
            eachData.service_type_name !== "null"
          ) {
            name += eachData.service_type_name + "- ";
          }

          name += eachData.service_offering_name;

          prices.push({
            name: name,
            service_id: eachData.service_id,
            service_name: eachData.service_name,
            service_type_id: eachData.service_type_id,
            service_type_name: eachData.service_type_name,
            service_offering_id: eachData.service_offering_id,
            service_offering_name: eachData.service_offering_name,
            amount: eachData.amount
          });

          total = parseFloat(total) + parseFloat(eachData.amount || 0);
        }
      }

      //  ADD CUSTOM SERVICES TOTAL
      // Preserve custom services already in priceSection (from backend or UI)
      const existingCustomServices =
        basis?.filter((item) => item?.isCustom) || [];

      const customTotal = existingCustomServices.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      );

      // Staged inventory parts (new booking) - rows the vendor added via
      // StagedPartsSection, preserved across rebuilds exactly like custom
      // items above.
      const existingParts = basis?.filter((item) => item?.isPart) || [];

      const partsTotal = existingParts.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      );

      // Live parts subtotal lifted up from PartsUsedSection (edit booking) -
      // those rows already live in ezp_booking_parts, not in priceSection,
      // so they're folded in as a plain number rather than spread rows.
      total =
        parseFloat(total) +
        parseFloat(customTotal || 0) +
        parseFloat(partsTotal || 0) +
        parseFloat(editPartsSubtotal || 0);

      const taxAmount = (parseFloat(total) * taxPercentage) / 100;
      if (
        prices?.length > 0 ||
        customTotal > 0 ||
        partsTotal > 0 ||
        editPartsSubtotal > 0
      ) {
        prices = [
          ...prices,
          {
            name: "Discount",
            amount: discount
          },
          ...existingCustomServices,
          ...existingParts,
          {
            name: `Tax (${taxPercentage}%)`,
            amount: taxAmount.toFixed(2)
          }
        ];

        let t1 = parseFloat(total) + taxAmount - parseFloat(discount || 0);

        prices.push({
          name: "Total",
          amount: parseFloat(t1).toFixed(2)
        });
      }

      setPriceSection(prices);
    } catch (e) {
      console.log("e-----", e);
    }
  }

  async function arrangeData(d1) {
    // serviceData also carries offerings added under other Service
    // Categories (multi-category bookings) — the Sub Category / Service Type
    // grid only shows the selected category's own offerings.
    const selId = selectedService?.service_id;
    let g1 = serviceData.reduce((acc, item) => {
      if (selId != null && String(item?.service_id) !== String(selId)) {
        if (d1) item.added = false;
        return acc;
      }
      // console.log("acc--", acc);
      let { service_type_name } = item;
      // console.log(service_type_name);
      if (!acc[service_type_name]) acc[service_type_name] = [];

      if (d1) item.added = false;
      acc[service_type_name].push(item);
      return acc;
    }, {});

    setServiceTypes(Object.keys(g1));
    setGroupedData(g1);
  }

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([
          fetchCountries(),
          getBookingStatus(),
          getPaymentStatus()
        ]);
      } catch (error) {
        console.error("Error initializing data:", error);
        toast.error("Failed to initialize form data");
      }
    };

    initializeData();
  }, []);

  const fetchServiceData = async (data, partnerID = null) => {
    const requestId = ++latestServiceFetchRef.current;
    try {
      // When coming back from Checkout via "+ Add Item", reuse saved service data
      if (checkoutReturnServiceDataRef.current) {
        const saved = checkoutReturnServiceDataRef.current;
        checkoutReturnServiceDataRef.current = null;
        let sumTime = 0;
        for (const i of saved) {
          if (i?.added) sumTime += (i?.time_required || 0) + (i?.buffer || 0);
        }
        setMaxTime(sumTime);
        setServiceData(saved);
        return;
      }
      if (data?.service_id) {
        // An explicitly-passed partnerID (e.g. the Profile dropdown's onChange,
        // which fires fetchServiceData in the same handler as setPartner_id)
        // must win over the partner_id state, which is still the OLD value
        // here — setPartner_id hasn't taken effect yet when this line runs, so
        // preferring state over the parameter fetched Sub Category/Service Type
        // for the partner being switched AWAY from, not the one being switched to.
        let p1 = partnerID != null ? partnerID : partner_id || 1;
        setLoading(true);
        const response = await apicall.get(
          `offeringFromService?service_id=${data?.service_id}&&is_partner=true&&partner_id=${p1}`
        );
        setLoading(false);

        // A newer call (e.g. the real booking data loading in) has since
        // started — this response is stale, don't let it clobber the correct
        // Service Category/Sub Category/Service Type selection.
        if (requestId !== latestServiceFetchRef.current) return;

        // console.log("existingData----->>>>", existingData);
        let copyResponse;
        if (response?.data) {
          copyResponse = JSON.parse(JSON.stringify(response?.data));
          // offeringFromService returns both active and inactive offerings
          // (Admin Setup needs to show inactive ones so they can be
          // reactivated) — while booking, only active ones should be
          // selectable here.
          copyResponse = copyResponse.filter(
            (item) => Number(item.is_active) === 1
          );
        }

        // A booking can hold offerings from several Service Categories, but
        // this fetch only returns the offerings of ONE category — replacing
        // serviceData with it wiped out whatever was added under the category
        // the vendor just switched away from. Carry the other categories'
        // offerings (and this category's own in-session add/remove/price
        // edits) forward from the current serviceData, unless the booking
        // being edited (existingData) or the Profile changed since the last
        // fetch — then start fresh from existingData like before.
        const keepPrev =
          lastServiceFetchKeyRef.current.existingData === existingData &&
          String(lastServiceFetchKeyRef.current.partner) === String(p1);
        lastServiceFetchKeyRef.current = { existingData, partner: p1 };
        const prevList = keepPrev ? serviceDataRef.current || [] : [];
        const currentServiceId = String(data.service_id);

        let offering_id = [];
        let type_name;

        if (response.success && copyResponse) {
          for (let i of existingData) {
            for (let j of copyResponse) {
              if (i.service_offering_id === j.service_offering_id) {
                // console.log("is there-----");
                j.added = true;
                if (!offering_id.includes(i.service_offering_id)) {
                  j.amount = Number(i.price);
                  offering_id.push(i.service_offering_id);
                }
                break;
              }
            }
          }
          // console.log("offering_id---", offering_id);
          // console.log("copyResponse---", copyResponse);

          let c1 = copyResponse?.map((ele) => {
            if (offering_id.includes(ele?.service_offering_id)) {
              if (!type_name) {
                type_name = ele?.service_type_name;
                setActiveType(ele?.service_type_name);
              }

              return { ...ele, added: true };
            } else {
              return { ...ele, added: false };
            }
          });

          let sumTime = 0;
          for (let i of copyResponse) {
            if (i?.added) {
              let t1 = i?.time_required + i?.buffer;
              sumTime += t1;
            }
          }
          setMaxTime(sumTime);

          const pendingSub = pendingSubRef.current;
          if (pendingSub) {
            pendingSubRef.current = null;
            const subOfferingId = pendingSub.service_offering_id;
            c1 = c1.map((item) => ({
              ...item,
              added: item.service_offering_id === subOfferingId
            }));
            setActiveType(pendingSub.service_type_name || "null");
          } else {
            const prevById = new Map(
              prevList.map((i) => [String(i.service_offering_id), i])
            );
            // Restore this category's selection from earlier in the session
            // (vendor added/removed/re-priced here, switched away, came back).
            if (keepPrev) {
              c1 = c1.map((item) => {
                const p = prevById.get(String(item.service_offering_id));
                return p ? { ...item, added: p.added, amount: p.amount } : item;
              });
            }

            let others;
            if (keepPrev) {
              others = prevList.filter(
                (i) => String(i.service_id) !== currentServiceId
              );
            } else {
              // Fresh load: existingData rows from the booking's OTHER
              // categories haven't been fetched yet — seed them from the
              // booking rows themselves so they stay on the booking (and in
              // the price summary) until the vendor opens that category.
              const seen = new Set();
              others = existingData
                .filter((r) => {
                  const k = String(r?.service_offering_id);
                  if (
                    !r?.service_offering_id ||
                    String(r.service_id) === currentServiceId ||
                    seen.has(k)
                  )
                    return false;
                  seen.add(k);
                  return true;
                })
                .map((r) => ({
                  service_id: r.service_id,
                  service_name: r.service_name,
                  service_type_id: r.service_type_id,
                  service_type_name: r.service_type_name,
                  service_offering_id: r.service_offering_id,
                  service_offering_name: r.service_offering_name,
                  time_required: r.time_required,
                  buffer: r.buffer,
                  price: Number(r.price),
                  amount: Number(r.price),
                  is_active: 1,
                  added: true
                }));
            }
            c1 = [...c1, ...others];

            const firstAdded = c1.find(
              (i) => i.added && String(i.service_id) === currentServiceId
            );
            const nextType = firstAdded
              ? firstAdded.service_type_name
              : copyResponse[0]?.service_type_name;
            setActiveType(nextType ? nextType : "null");
          }

          let finalSumTime = 0;
          for (let i of c1) {
            if (i?.added)
              finalSumTime += (i?.time_required || 0) + (i?.buffer || 0);
          }
          setMaxTime(finalSumTime);
          setServiceData(c1);
        } else {
          // Don't drop offerings already added under other categories just
          // because this one has none.
          setServiceData(
            prevList.filter((i) => String(i.service_id) !== currentServiceId)
          );
        }
      }
    } catch (error) {
      console.log("err---", error);
      toast.error("Failed to fetch service offerings....");
      setInitialServiceData([]);
      setServiceData([]);
    } finally {
      setLoading(false);
    }
  };
  const crewCount = providerSelected?.length || 0;

  // Optional resource field: shown only when the vendor has resources (or this
  // booking already holds one that has since been deactivated).
  const showResourceField = activeResources.length > 0 || !!resourceIds;
  const resourceLabel = isSportsService
    ? "Court / Field"
    : isCarService
    ? "Bay / Equipment"
    : isSalonService
    ? "Chair / Station"
    : "Resource";
  const resourceCrewIds = [
    ...new Set(
      (providerSelected || [])
        .map((p) => p?.extendedProps?.provider_id)
        .filter((id) => id !== undefined && id !== null && id !== "")
    )
  ];
  // Same wall-clock rule the server uses: the crew work in parallel, so the
  // resource is held for the total work divided across them.
  const resourceTime = (
    providerSelected?.[0]?.start?.split?.("T")?.[1] ||
    timeSelect ||
    ""
  ).slice(0, 5);
  const resourceDurationMin = Math.max(
    Math.round((maxTime || 0) / Math.max(resourceCrewIds.length, 1)),
    30
  );

  const serviceDuration = maxTime / 60;
  // groupedData?.[activeType]?.find((d) => d.added)?.time_required / 60 || 0;

  const durationPerCleaner = maxTime / crewCount;
  // crewCount > 0 ? Math.ceil((serviceDuration * 60) / crewCount) : 0;
  const isDataReady = maxTime > 0 && serviceData?.length > 0;

  const totalReservedHours = maxTime / 60;
  // crewCount > 0 ? (crewCount * serviceDuration).toFixed(2) : 0;

  const fetchPartners = async () => {
    try {
      const response = await apicall.get("partners");
      setPartners(response.data);

      if (!group_id && !customerSelectedRef.current) {
        setPartner_id(response?.data[0]?.id);
        // Functional update - payment deliberately left out here so it
        // doesn't stomp whatever getPaymentStatus's own default-selection
        // sets (that effect runs independently/in parallel with this one,
        // and either can resolve first).
        setInitialValues((prev) => ({
          ...prev,
          partner: response?.data[0]?.id,
          org_id: "",
          userName: "",
          phoneNumber: "",
          email: "",
          house_no: "",
          area: "",
          location: "",
          location_id: "",
          state: "",
          landmark: "",
          country: vendorProfile?.currency === "INR" ? 2 : 1,
          lat: "",
          lng: "",
          status: 2 // In Progress — default service status for a new booking
        }));
      }
    } catch (error) {
      console.error("Error fetching fetchPartners:", error);
      toast.error("Failed to fetch fetchPartners");
    }
  };

  // Show only this vendor's industry-specific statuses when
  // ezp_booking_status has any (e.g. Car Service's own Booked/Checked
  // In/.../Ready for Delivery set), and only fall back to the generic
  // (industry IS NULL) rows - never the whole table across every
  // industry - when none are configured. See utils/bookingStatus.js.
  const getBookingStatus = async () => {
    try {
      const profileRes = await apicall.get("ezpMyProfile");
      const vendorIndustry = profileRes?.data?.[0]?.industry;
      setBookingStatus(await fetchBookingStatusesForIndustry(apicall.get, vendorIndustry));
    } catch (error) {
      console.error("Error fetching Booking Status:", error);
      toast.error("Failed to fetch Booking Status:");
    }
  };

  const getRecentBookings = async (ph_number) => {
    try {
      if (!ph_number) {
        setRecentBookings([]);
        return;
      }
      const response = await apicall.get(
        `getRecentBookings/${ph_number}/${vendor_id}`
      );

      if (response.success && response.data) {
        setRecentBookings(response.data || []);
      } else {
        setRecentBookings([]);
      }
    } catch (error) {
      console.error("Error fetching Booking Status:", error);
      toast.error("Failed to fetch Booking Status:");
    }
  };

  const getPaymentStatus = async () => {
    try {
      const response = await apicall.get("get_ezp_payment_status");
      if (response.success) {
        setPaymentStatus(response.data);
        // New booking only (keyed off group_id, not touching edit mode at
        // all) - defaults to the first option in the list so the dropdown
        // isn't blank; the vendor can still change it before submitting.
        if (!group_id && response.data?.length) {
          setInitialValues((prev) => ({ ...prev, payment: response.data[0].id }));
        }
      } else {
        setPaymentStatus([]);
      }
    } catch (error) {
      console.error("Error fetching Booking Status:", error);
      toast.error("Failed to fetch Booking Status:");
    }
  };

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await apicall.get("country", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.success) {
        const countriesData = Array.isArray(response.data)
          ? response.data
          : response.data?.countries || [];

        setCountries(countriesData);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Failed to fetch countries");
    }
  };

  const fetchStates = async (countryId1) => {
    try {
      // console.log("countryId----", countryId);
      let countryId = currency === "INR" ? 2 : 1;
      const token = localStorage.getItem("token");
      const response = await apicall.get(`state/${countryId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.success && response.data) {
        setStates(response.data);
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
      setStates([]);
    }
  };

  const handleCountryChange = (e, setFieldValue) => {
    const countryId = e.value;

    setFieldValue("country", countryId);
    if (countryId) {
      fetchStates(countryId);
      // Reset state and city when country changes
      // setStates([]);
      // setCities([]);
      // setFieldValue("state", "");
      // setFieldValue("area", "");
    } else {
      // setStates([]);
      // setCities([]);
    }
  };
  useEffect(() => {
    if (initialValues.country) {
      fetchStates(initialValues.country);
    }
  }, [initialValues.country]);
  const handleStateChange = (e, setFieldValue) => {
    const stateId = e.target.value;

    setFieldValue("state", stateId);
    if (stateId) {
      // fetchCities(stateId);
      // Reset city when state changes
      // setCities([]);
      // setFieldValue("city", "");
    } else {
      // setCities([]);
    }
  };

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const get_user_details = async (ph_number, setFieldValue, setValues) => {
    const requestId = ++latestUserLookupRef.current;
    const response = await apicall.get(
      `get_users_details/${ph_number}`
    );

    // console.log("first---response---", response);

    // A newer lookup (user kept typing / corrected the number after an
    // earlier pause) has since started. Without this guard, a slow, stale
    // response for an old/incomplete number can resolve after the correct
    // one and silently wipe out the just-filled customer name (its "not
    // found" branch calls setFieldValue("userName", "")), which then fails
    // Yup's "required" check on submit even though the name was visibly
    // filled in moments earlier.
    if (requestId !== latestUserLookupRef.current) return;

    if (response.status === 200 && response.data.length > 0) {
      const user = response.data[0];
      const fullName = `${user.first_name} ${user.middle_name || ""} ${
        user.last_name || ""
      }`.trim();
      // const age = calculateAge(user.dob);
      const gender =
        user.gender === null
          ? "Not Available"
          : user.gender === 1
            ? "Male"
            : "Female";

      setUser_id(user?.id || 0);
      customerSelectedRef.current = true;
      setSelectedSubscription(null);

      // Fetch active Open+Flexi subscriptions for this customer
      if (user.id) {
        apicall
          .get(`user-flexi-subscriptions/${user.id}`)
          .then((res) => {
            setUserFlexiSubs(Array.isArray(res.data) ? res.data : []);
          })
          .catch((err) => {
            console.warn(
              "Flexi subscription fetch failed:",
              err?.message || err
            );
            setUserFlexiSubs([]);
          });
      }

      // Batch every field into a single update so Formik only runs ONE
      // validation pass, against the complete, correct values. Firing 11
      // separate setFieldValue calls each queued its own async validation —
      // clicking Checkout before the LAST one resolved could still catch a
      // stale "Customer name is required" result from earlier in the
      // sequence, even though userName was already correctly set.
      setValues((prev) => ({
        ...prev,
        userName: fullName,
        email: cleanBackendValue(user?.email),
        house_no: cleanBackendValue(user?.house_no),
        area: cleanBackendValue(user?.area),
        landmark: cleanBackendValue(user?.landmark),
        state: cleanBackendValue(user?.state),
        location: cleanBackendValue(user?.location),
        lat: user?.lat || "",
        lng: user?.lng || "",
        gender: gender,
        location_id: user?.location_id || ""
      }));

      let d1 = {
        house_no: user?.house_no || "",
        area: user?.area || "",
        location: user?.location || "",
        state: user?.state || "",
        landmark: user?.landmark || "",
        lat: user?.lat || "",
        lng: user?.lng || ""
      };
      setFirstLocation(d1);

      // setSelectedUserId(user.id);

      // // Also update formData state for consistency
      // setFormData((prev) => ({
      //   ...prev,
      //   userName: fullName,
      //   age: age.toString(),
      //   gender: gender,
      // }));
    } else {
      setValues((prev) => ({ ...prev, userName: "", age: "", gender: "" }));
      setUserFlexiSubs([]);
      setSelectedSubscription(null);
      customerSelectedRef.current = false;
    }
  };

  const debouncedCheckUserExists = useCallback(
    debounce(async (field, value, setFieldValue, setValues) => {
      try {
        get_user_details(value, setFieldValue, setValues);
        getRecentBookings(value, setFieldValue);
      } catch (error) {
        console.error(`Error in debounced check for ${field}:`, error);
      }
    }, 500),
    []
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      console.log("handleSubmit---");
      let flag = true;

      if (!services || services.length === 0) {
        toast.error(
          "Please add Service Type and Offerings from Admin Setup before booking."
        );
        return;
      }

      if (!serviceTypes || serviceTypes.length === 0) {
        toast.error("Please configure Service Offerings before proceeding.");
        return;
      }
      let addedService = serviceData?.filter((ele) => ele?.added);
      if (addedService?.length === 0) {
        toast.error("Select Service Offerings to proceed");
        flag = false;
      }

      if (providerSelected?.length === 0) {
        toast.error(
          isCarService || isSalonService
            ? "Select Time and Service Provider"
            : "Select Time and Crew"
        );
        flag = false;
      }

      if (!flag) {
        return;
      }

      console.log("priceSection-----", priceSection);

      let d1 = {
        house_no: values?.house_no || "",
        area: values?.area || "",
        location: values?.location || "",
        state: values?.state || "",
        landmark: values?.landmark || "",
        lat: values?.lat || "",
        lng: values?.lng || ""
      };
      // setFirstLocation(d1);
      // console.log("d1------", d1, firstLocation);

      let isLocationChanged = false;
      if (JSON.stringify(d1) !== JSON.stringify(firstLocation)) {
        isLocationChanged = true;
      }

      let providers = providerSelected?.map(
        (ele) => ele?.extendedProps?.provider_id
      );

      values.existingUserId = user_id;
      values.editable = editable ? 1 : 0;
      values.subscription_id = selectedSubscription
        ? selectedSubscription.user_subscription_id
        : existingSubscriptionId || null;

      values.date = dateSelect;
      values.isLocationChanged = isLocationChanged;
      const t1Parts = providerSelected[0]?.start?.split("T")?.[1]?.split(":");
      values.time = t1Parts
        ? `${t1Parts[0]}:${t1Parts[1]}`
        : timeSelect || "00:00";
      values.serviceData = serviceData;
      values.priceSection = priceSection;
      values.providers = providers?.join(", ");
      // Only when the field is shown. "" clears; omitting the key leaves the
      // booking's resource untouched on the server.
      if (showResourceField) values.resource_ids = resourceIds;
      // Only for a service that has a workflow; null for a step left empty so a
      // person who was taken off really is removed.
      if (formWorkflow) {
        values.workflow = buildWorkflowPayload(formWorkflow, workflowAssign);
      }
      // Snapshot providerSelected so back-navigation from Checkout can restore crew selection
      values.providerSelectedData = providerSelected.map((ev) => ({
        provider_id: ev?.extendedProps?.provider_id,
        start:
          ev?.start instanceof Date
            ? ev.start.toISOString()
            : String(ev?.start || ""),
        end:
          ev?.end instanceof Date
            ? ev.end.toISOString()
            : String(ev?.end || ""),
        title: ev?.title || ""
      }));
      values.group_id = group_id;
      // Carried through to Checkout.jsx so it can call
      // estimationService.markConverted() once the booking this estimation
      // is being converted into actually gets created.
      if (fromEstimationId) values.from_estimation = fromEstimationId;
      values.additional_notes = additionalNotes;
      values.paid_amount = paidAmount;
      values.currency = currency;

      if (group_id) {
        // Service Specifics diff against what was loaded for this booking
        // (an unreported baseline counts as empty). A staged new-asset draft
        // is always a pending change.
        const specificsBaseline = originalServiceSpecificsRef.current || normalizeSpecifics(null);
        const specificsNow = normalizeSpecifics(serviceSpecifics);
        const specificsChanged =
          specificsNow.asset !== specificsBaseline.asset ||
          specificsNow.complaint !== specificsBaseline.complaint ||
          specificsNow.action !== specificsBaseline.action ||
          !!serviceSpecifics.new_asset_draft;

        // Detect if anything actually changed before submitting
        if (originalSnapshotRef.current) {
          const snap = originalSnapshotRef.current;
          const currentOfferingIds = serviceData
            .filter((d) => d.added)
            .map((d) => d.service_offering_id)
            .sort()
            .join(",");
          const currentProvidersStr = providerSelected
            .map((p) => `${p.extendedProps?.provider_id}:${p.start}`)
            .sort()
            .join(",");
          const currentTime = (
            providerSelected[0]?.start?.split("T")?.[1] ||
            timeSelect ||
            ""
          ).slice(0, 5);

          const unchanged =
            (values.userName || "") === snap.user_name &&
            String(values.phoneNumber || "") === snap.phone_number &&
            (values.email || "") === snap.email &&
            (values.house_no || "") === snap.house_no &&
            (values.area || "") === snap.area &&
            (values.location || "") === snap.location &&
            (values.state || "") === snap.state &&
            (values.landmark || "") === snap.landmark &&
            String(values.lat || "") === snap.lat &&
            String(values.lng || "") === snap.lng &&
            dateSelect === snap.service_date &&
            currentTime === snap.service_time &&
            (additionalNotes || "") === snap.additional_info &&
            Number(discount || 0) === snap.discount &&
            Number(paidAmount || 0) === snap.total_paid_amount &&
            Number(values.status || 0) === snap.status &&
            Number(values.payment || 0) === snap.payment &&
            currentOfferingIds === snap.offering_ids &&
            currentProvidersStr === snap.providers &&
            !specificsChanged &&
            (!showResourceField || (resourceIds || "") === (snap.resource_ids || "")) &&
            (!formWorkflow ||
              assignmentsSignature(formWorkflow, workflowAssign) ===
                workflowInitialSigRef.current);

          if (unchanged) {
            if (!partsChangedRef.current) {
              toast.info(
                "No changes detected. Please modify booking details before updating."
              );
              setSubmitting(false);
              return;
            }
            // Only parts changed: fall through to the normal update submit
            // below instead of returning early, since parts now count toward
            // the booking's own total/Balance Due and that must actually be
            // persisted - values.priceSection (set just above) already
            // reflects it via editPartsSubtotal. The submit's own success
            // path ("Order Updated") covers the feedback that used to come
            // from a dedicated toast here.
          }
        }

        // Edit mode: update the booking directly without going to Checkout
        setLoading(true);
        // No user_id / vendor_id here: the server takes the vendor from the login
        // (a staff login's own id would save the edit under the wrong vendor).
        // Respect whatever the vendor picked in the Status/Payment dropdowns
        // (e.g. marking as Completed / Paid); only fall back to the
        // existing/default value if nothing was selected. The payment line
        // below used to always take existingData's stale value regardless
        // of what was picked - that's why payment kept saving as whatever
        // it originally was (1, if existingData's own payment came back
        // empty) no matter what the vendor selected.
        values.status = values.status || existingData[0]?.status || 2;
        values.payment = values.payment ?? existingData[0]?.payment ?? 1;

        const currentPartnerId = Number(values.partner || partner_id);
        const currentPartner = partners?.find((p) => p.id === currentPartnerId);
        const editApiName =
          currentPartner?.partner_name?.trim().toLowerCase() === "utilstop"
            ? "create_partner_booking"
            : "create_ezp_booking";

        const bookingRes = await apicall.post(values, editApiName);
        setLoading(false);
        if (bookingRes?.message && !bookingRes?.booking_id) {
          toast.error(bookingRes.message);
        } else if (bookingRes?.booking_id) {
          // Service Specifics (asset selection + complaint/action-taken note)
          // lives in a separate table - the booking id is already known here
          // (existingData[0].id), no need to wait on bookingRes for it. Only
          // fires if it differs from what was loaded, so ordinary bookings
          // with no configured asset schema don't create empty rows - and
          // clearing the asset/notes still persists (a truthy-only check
          // silently dropped removals).
          if (specificsChanged) {
            apicall.post({ booking_id: existingData[0].id, ...serviceSpecifics }, "ezp_booking_service_details")
              .then((r) => {
                if (r?.success) originalServiceSpecificsRef.current = specificsNow;
              })
              .catch((e) => console.log(e));
          }
          setModelData({
            booking_id: bookingRes.booking_id,
            serviceData: values.serviceData,
            state: values.state,
            date: values.date,
            time: values.time,
            lat: values.lat,
            lng: values.lng,
            house_no: values.house_no,
            area: values.area,
            location: values.location,
            landmark: values.landmark,
            userName: values.userName,
            phoneNumber: values.phoneNumber
          });
          if (bookingRes.resource_conflicts) {
            toast.warn(
              `Saved, but the ${resourceLabel.toLowerCase()} is also booked at this time: ${describeConflict(
                bookingRes.resource_conflicts
              )}`
            );
          }
          if (bookingRes.workflow_warning) toast.warn(bookingRes.workflow_warning);
          if (formWorkflow) {
            // What was just saved is the new starting point for "did anything change", and
            // the panel should show who is really on each step now.
            workflowInitialSigRef.current = assignmentsSignature(formWorkflow, workflowAssign);
            getBookingWorkflow(group_id)
              .then((res) => {
                if (res?.success && res.data) setRunWorkflow(res.data);
              })
              .catch(() => {});
          }
          setShowOrderConf(true);
        } else {
          toast.error("Failed to update booking. Please try again.");
        }
      } else {
        // New booking: go to Checkout to select payment method. The booking
        // doesn't exist yet, so Service Specifics can't be saved until
        // Checkout.jsx actually creates it - carry the values through on
        // bookingValues and let Checkout.jsx fire the upsert once it has a
        // real booking id.
        if (serviceSpecifics.new_asset_error) {
          setLoading(false);
          toast.error(serviceSpecifics.new_asset_error);
          return;
        }
        values.customer_asset_id = serviceSpecifics.customer_asset_id;
        values.new_asset_draft = serviceSpecifics.new_asset_draft;
        values.customer_complaint = serviceSpecifics.customer_complaint;
        values.action_taken = serviceSpecifics.action_taken;
        navigate("/checkout", {
          state: {
            bookingValues: values,
            partners: partners,
            partner_id: partner_id
          }
        });
      }
    } catch (error) {
      console.log("API Error:", error);
      setLoading(false);
      toast.error(error?.message || "Failed to create booking");
    }
  };

  const addOffering = (data) => {
    try {
      // console.log("data-----", data);
      let t1 = data?.time_required + data?.buffer;

      // if (t1 > maxTime) {
      //   setMaxTime(t1);
      // }
      let { service_offering_id } = data;
      let copyServiceData = JSON.parse(JSON.stringify(serviceData));
      for (let eachdata of copyServiceData) {
        if (eachdata?.service_offering_id === service_offering_id) {
          eachdata.added = !eachdata.added;
          break;
        }
      }

      let sumTime = 0;
      for (let i of copyServiceData) {
        if (i?.added) {
          let t1 = i?.time_required + i?.buffer;
          sumTime += t1;
        }
      }
      setMaxTime(sumTime);
      setServiceData(copyServiceData);
    } catch (e) {
      console.log("err----", e);
    }
  };
  const totalItem = priceSection?.find((item) => item.name === "Total");
  const totalAmount = Number(totalItem?.amount || 0);

  const balanceDue = totalAmount - Number(paidAmount || 0);

  // Edit booking only: how much this session's part changes have added to
  // the balance due, tax-adjusted to match how getPrice() actually taxes the
  // combined total. Baselined against whatever the booking's parts subtotal
  // already was when opened (originalPartsSubtotalRef), so pre-existing
  // parts don't trigger this - only parts added/removed during this visit.
  const pendingBalanceIncrease =
    originalPartsSubtotalRef.current === null
      ? 0
      : (editPartsSubtotal - originalPartsSubtotalRef.current) * (1 + taxPercentage / 100);

  function priceChange(value, data) {
    try {
      let copyServiceData = JSON.parse(JSON.stringify(serviceData));

      for (let eachData of copyServiceData) {
        if (eachData.service_offering_id === data?.service_offering_id) {
          eachData.amount = value;
        }
      }
      if (data?.name === "Discount") {
        setDiscount(value);
      }

      setServiceData(copyServiceData);
    } catch (e) {
      console.log("err-----", e);
    }
  }
  function deleteCustomItem(index) {
    const copy = [...priceSection];

    //  remove item
    copy.splice(index, 1);

    //  update UI immediately
    setPriceSection(copy);

    //  temporarily override priceSection for calculation
    const existingCustomServices = copy.filter((item) => item?.isCustom) || [];
    // Staged inventory parts must survive this rebuild too, or deleting an
    // unrelated custom line item would silently wipe out any staged parts.
    const existingParts = copy.filter((item) => item?.isPart) || [];

    //  manual trigger (same logic as getPrice but with updated copy)
    try {
      let prices = [];
      let total = 0;

      // services
      for (let eachData of serviceData) {
        if (eachData?.added) {
          let name = eachData.service_name + "- ";

          if (
            eachData.service_type_name &&
            eachData.service_type_name !== "null"
          ) {
            name += eachData.service_type_name + "- ";
          }

          name += eachData.service_offering_name;

          prices.push({
            name,
            amount: eachData.amount
          });

          total += Number(eachData.amount || 0);
        }
      }

      //  use UPDATED custom items
      const customTotal = existingCustomServices.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      );
      const partsTotal = existingParts.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      );

      total += customTotal + partsTotal + Number(editPartsSubtotal || 0);

      const taxAmount = (total * taxPercentage) / 100;

      let finalTotal = total - Number(discount || 0);

      prices = [
        ...prices,
        { name: "Discount", amount: discount },
        ...existingCustomServices,
        ...existingParts,
        {
          name: `Tax (${taxPercentage}%)`,
          amount: taxAmount.toFixed(2)
        }
      ];

      finalTotal += taxAmount;

      prices.push({
        name: "Total",
        amount: finalTotal.toFixed(2)
      });

      setPriceSection(prices);
    } catch (e) {
      console.log(e);
    }
  }
  function addCustomService() {
    const copy = [...priceSection];

    const taxIndex = copy.findIndex((item) =>
      item.name?.toLowerCase().includes("tax")
    );

    const newRow = {
      name: "",
      amount: "",
      isCustom: true,
      id: Date.now()
    };

    if (taxIndex !== -1) {
      copy.splice(taxIndex, 0, newRow); //  insert BEFORE tax
    } else {
      copy.push(newRow);
    }

    setPriceSection(copy);
  }

  function customNameChange(value, index) {
    const copy = [...priceSection];
    copy[index].name = value;
    setPriceSection(copy);
  }
  function customPriceChange(value, index) {
    const copy = [...priceSection];
    copy[index].amount = value;
    setPriceSection(copy);

    getPrice(copy);
  }

  // Staged inventory parts for a brand-new booking (StagedPartsSection).
  // Live purely as rows in priceSection - tagged isPart, mirroring how
  // isCustom rows already work - so they fold into getPrice()'s total the
  // same way, and survive unchanged into Checkout.jsx via
  // values.priceSection. Nothing is persisted here; the real
  // addBookingPart(...) calls happen only after the booking is actually
  // created (Checkout.jsx's flushStagedParts).
  function addStagedPart(item, quantity, unitPrice) {
    const qty = Number(quantity);
    const price = Number(unitPrice);
    const existingIdx = priceSection.findIndex(
      (row) => row?.isPart && row.item_id === item.id
    );
    let copy;
    if (existingIdx !== -1) {
      // Staging the same item again merges into the existing line (combined
      // quantity, latest unit price) rather than adding a duplicate row.
      const mergedQuantity = Number(priceSection[existingIdx].quantity) + qty;
      const merged = {
        ...priceSection[existingIdx],
        quantity: mergedQuantity,
        unit_price: price,
        amount: mergedQuantity * price
      };
      copy = [...priceSection];
      copy[existingIdx] = merged;
    } else {
      const newRow = {
        name: item.name,
        amount: qty * price,
        isPart: true,
        item_id: item.id,
        quantity: qty,
        unit_price: price,
        unit_of_measure: item.unit_of_measure,
        id: `part-${item.id}-${Date.now()}`
      };
      copy = [...priceSection, newRow];
    }
    setPriceSection(copy);
    getPrice(copy);
  }

  function removeStagedPart(rowId) {
    const copy = priceSection.filter((row) => row.id !== rowId);
    setPriceSection(copy);
    getPrice(copy);
  }

  const onHide = () => {
    setShowCalender(false);
    flagCount.current = 0;
  };

  function convertWithoutTimseStamp(date) {
    try {
      const year = date?.getFullYear();
      const month = String(date?.getMonth() + 1).padStart(2, "0");
      const day = String(date?.getDate()).padStart(2, "0");
      const hours = String(date?.getHours()).padStart(2, "0");
      const minutes = String(date?.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}:00`;
    } catch (e) {
      console.log("err---", date, e);
    }
  }

  const cleanCrewSlots = (slots) => {
    const grouped = {};

    // Step 1: Group by provider_id
    slots.forEach((slot) => {
      const id = slot.extendedProps.provider_id;
      if (!grouped[id]) grouped[id] = [];
      grouped[id].push(slot);
    });

    let result = [];

    // Step 2: Process each crew separately
    Object.values(grouped).forEach((group) => {
      const bookedSlots = group.filter((s) => s.status === 2);

      const cleaned = group.filter((slot) => {
        // keep booked slots
        if (slot.status === 2) return true;

        // remove overlap only within same crew
        const isOverlapping = bookedSlots.some((booked) => {
          return (
            new Date(slot.start) < new Date(booked.end) &&
            new Date(slot.end) > new Date(booked.start)
          );
        });

        return !isOverlapping;
      });

      result.push(...cleaned);
    });

    return result;
  };

  function hasOverlapWithBooked(slot, proposedEnd, bookedSlots) {
    const slotStart = new Date(slot.start);

    for (const booked of bookedSlots) {
      if (
        booked.extendedProps?.provider_id !== slot.extendedProps?.provider_id
      ) {
        continue;
      }

      const bookedStart = new Date(booked.start);
      const bookedEnd = new Date(booked.end);

      // proposed window straddles or overlaps the booked window
      const endLandsInside =
        proposedEnd > bookedStart && proposedEnd < bookedEnd;
      const startBeforeEnd = slotStart < bookedStart && proposedEnd > bookedEnd;

      if (endLandsInside || startBeforeEnd) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true when the slot overlaps any break slot for the same provider.
   */
  function hasOverlapWithBreaks(slot, proposedEnd, breakSlots) {
    const slotStart = new Date(slot.start);

    for (const brk of breakSlots) {
      if (brk.extendedProps?.provider_id !== slot.extendedProps?.provider_id) {
        continue;
      }

      const breakStart = new Date(brk.start);
      const breakEnd = new Date(brk.end);

      // Only an actual overlap between [slotStart, proposedEnd) and
      // [breakStart, breakEnd) should block selection — a break earlier
      // (or later) in the day that the proposed window doesn't touch
      // must not block it.
      if (slotStart < breakEnd && proposedEnd > breakStart) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true when the proposed end time exceeds the last available slot end
   * for this provider.
   */
  function exceedsProviderAvailability(appointment, proposedEnd, allSlots) {
    const providerId = appointment.details?.extendedProps?.provider_id;

    const maxProviderEnd = allSlots
      .filter((s) => s.extendedProps?.provider_id === providerId)
      .map((s) => new Date(s.end))
      .reduce((max, curr) => (curr > max ? curr : max), new Date(0));

    return proposedEnd > maxProviderEnd;
  }

  // ─────────────────────────────────────────────────────────────────────────────

  const handleAppointmentClick = (appointment) => {
    try {
      // ── Guard: holidays and already-blocked slots are not selectable ────────
      if (appointment?.details?.extendedProps?.isHoliday) return;
      if ([3, 6].includes(appointment?.details?.status)) return;

      // ── Derive working copies ───────────────────────────────────────────────
      const slots = JSON.parse(JSON.stringify(filteredAppointments));
      const bookedSlots = slots.filter((s) => s.status === 3 || s.status === 6);
      const breakSlots = slots.filter((s) => s.status === 6);

      const clickedStart = appointment.details?.start;
      const clickedProviderId = appointment.details?.extendedProps?.provider_id;
      const slotDuration = maxTime / (crewCount + 1); // minutes

      setDateSelect(clickedStart?.split("T")?.[0]);

      // ── Nothing to resolve if the clicked slot is already booked ───────────
      if (appointment.details?.status === 3) {
        setProviderSelected([]);
        return;
      }

      // ── Walk every slot and assign statuses ────────────────────────────────
      const agentAssigned = [];

      for (const slot of slots) {
        // Never touch booked / break slots
        if ([3, 6].includes(slot.status)) continue;

        const sameStart = slot.start === clickedStart;
        const sameProvider =
          slot.extendedProps?.provider_id === clickedProviderId;

        if (!sameProvider) {
          // A different crew member's slot — leave their existing selection (2)
          // or availability (1) alone. Previously this branch was keyed on time
          // alone (!sameStart), which reset EVERY provider's other-time slots —
          // so selecting a second crew member at a different time silently wiped
          // out whichever crew member was already selected at their own time,
          // and only one crew's selection (and split duration) ever made it into
          // providerSelected / the saved booking.
          if (slot.status === 2) {
            agentAssigned.push(slot);
          }
          continue;
        }

        if (!sameStart) {
          // Same provider, different time → clear their previous selection
          // (a single provider can only have one active time slot at once)
          slot.status = 1;
          const end = new Date(slot.start);
          end.setMinutes(end.getMinutes() + 30);
          slot.end = end;
          continue;
        }

        if (sameStart && sameProvider && slot.status === 2) {
          // Same time, same provider, currently "pending" → revert to available
          slot.status = 1;
          const end = new Date(slot.start);
          end.setMinutes(end.getMinutes() + 30);
          slot.end = end;
          continue;
        }

        if (sameStart && sameProvider && slot.status === 1) {
          // This is the slot being selected → validate then mark as pending (2)
          const proposedEnd = new Date(slot.start);
          proposedEnd.setMinutes(proposedEnd.getMinutes() + slotDuration);

          const durationHours = slotDuration / 60;
          const insufficientMsg =
            `Not enough availability. This slot requires a full ${durationHours}-hour window. ` +
            `Please select a different start time.`;

          if (hasOverlapWithBooked(slot, proposedEnd, bookedSlots)) {
            setCalenderMessage(insufficientMsg);
            continue;
          }

          if (hasOverlapWithBreaks(slot, proposedEnd, breakSlots)) {
            setCalenderMessage(insufficientMsg);
            continue;
          }

          if (exceedsProviderAvailability(appointment, proposedEnd, slots)) {
            setCalenderMessage(insufficientMsg);
            continue;
          }

          slot.status = 2;
        }

        if (slot.status === 2) {
          agentAssigned.push(slot);
        }
      }

      // ── Merge selected slots back into the master list ─────────────────────
      const merged = JSON.parse(JSON.stringify(initialAppointments));

      for (let i = 0; i < merged.length; i++) {
        // Apply whatever status this slot ended up with above — not just a
        // newly-selected (2) one. Previously this only overlaid slots that
        // were selected, so toggling a selected slot back to available (1)
        // never made it into `merged`: the stale "selected" entry from
        // initialAppointments stuck around and the slot stayed stuck as
        // selected/blocked no matter how many times it was clicked.
        const match = slots.find(
          (s) =>
            s.start === merged[i].start &&
            s.extendedProps?.provider_id ===
              merged[i].extendedProps?.provider_id
        );
        if (match) merged[i] = JSON.parse(JSON.stringify(match));
      }

      setProviderSelected(agentAssigned);

      // ── Set end times on selected agents ───────────────────────────────────
      const timePerAgent =
        agentAssigned.length > 0 ? maxTime / agentAssigned.length : maxTime;

      for (const agent of agentAssigned) {
        if (agent.status === 2) {
          const end = new Date(agent.start);
          end.setMinutes(end.getMinutes() + timePerAgent);
          agent.end = convertWithoutTimseStamp(end);
        }
      }

      // ── Build the final visible slot list ──────────────────────────────────
      const visible = [];

      for (const slot of merged) {
        if ([2, 3].includes(slot.status)) {
          visible.push(slot);
          continue;
        }

        // Check whether this slot falls inside an agent's window
        const hiddenByAgent = agentAssigned.some((agent) => {
          const slotStart = convertWithoutTimseStamp(new Date(slot.start));
          const agentStart = convertWithoutTimseStamp(new Date(agent.start));
          const agentEnd = convertWithoutTimseStamp(new Date(agent.end));

          return (
            slot.extendedProps?.provider_id ===
              agent.extendedProps?.provider_id &&
            slotStart > agentStart &&
            slotStart < agentEnd
          );
        });

        if (hiddenByAgent) continue;

        // Always show all non-hidden, non-booked slots so the full day
        // view stays intact after every click (same behaviour as new booking flow).
        // The editable flag only controls the header toolbar – not slot visibility.
        visible.push(slot);
      }

      // ── Stamp end times on visible selected slots ───────────────────────────
      for (const slot of visible) {
        if (slot.status === 2) {
          const end = new Date(slot.start);
          end.setMinutes(end.getMinutes() + timePerAgent);
          slot.end = convertWithoutTimseStamp(end);
        }
      }

      // ── Compute time range for display ─────────────────────────────────────
      const cleaned = cleanCrewSlots(visible);

      if (!cleaned || cleaned.length === 0) {
        console.warn("handleAppointmentClick: no visible slots after clean");
        return;
      }

      const { minStart, maxEnd } = cleaned.reduce(
        (acc, s) => ({
          minStart: s.start < acc.minStart ? s.start : acc.minStart,
          maxEnd: s.end > acc.maxEnd ? s.end : acc.maxEnd
        }),
        { minStart: cleaned[0].start, maxEnd: cleaned[0].end }
      );

      const rawStart = minStart?.split("T")?.[1];
      const rawEnd = maxEnd?.split("T")?.[1];
      setTimeRange({
        startTime:
          !rawStart || rawStart.includes("NaN") ? "08:00:00" : rawStart,
        endTime: !rawEnd || rawEnd.includes("NaN") ? "20:00:00" : rawEnd
      });

      setFilteredAppointments(cleaned);
    } catch (err) {
      console.error("handleAppointmentClick error:", err);
    }
  };

  const datesSet = (dateInfo) => {
    try {
      let d1 = dateInfo.startStr?.split("T")?.[0];

      if (d1 != dateSelect) {
        getWorkerAvailableSlots(d1);
      }

      if (![0, 1].includes(flagCount.current)) {
        // console.log("adteIfo---", dateInfo);
      }
      flagCount.current += 1;
    } catch (e) {
      console.log("err---", e);
    }
  };

  const getWorkerAvailableSlots = async (selecDate) => {
    try {
      if (!selecDate) return;

      let d1 = new Date(selecDate);
      d1 = d1.toLocaleDateString("en-GB").split(/[\/\-\.]/);
      d1 = `${d1[2]}-${d1[1]}-${d1[0]}`;

      setLoading(true);

      // ===================== SLOT API =====================
      let q1 = `getWokerAvailableSlots?date=${d1}`;

      if (selectedService?.service_id) {
        q1 += `&&service_id=${selectedService.service_id}`;
      }

      const getWorkerAvailable = await apicall.get(q1);

      console.log("getWorkerAvailable----", getWorkerAvailable);

      let updatedData = Array.isArray(getWorkerAvailable?.data)
        ? JSON.parse(JSON.stringify(getWorkerAvailable.data))
        : [];

      setInitialAppointments(updatedData);

      // ===================== HOLIDAY API =====================
      let availabilityMap = {};

      if (selectedService?.service_id) {
        try {
          const holidayRes = await apicall.get(
            `get_service_crews_holidays?service_id=${selectedService.service_id}`
          );

          availabilityMap = holidayRes?.data?.crewWiseAvailability || {};
        } catch (err) {
          console.log("Holiday API failed", err);
          availabilityMap = {};
        }
      }

      // ===================== SAFE HELPER =====================
      const isCrewOnHoliday = (providerId, dateTime) => {
        if (!providerId || !dateTime) return false;

        if (!availabilityMap || Object.keys(availabilityMap).length === 0) {
          return false;
        }

        const date = dateTime.split("T")[0];
        return availabilityMap?.[providerId]?.includes(date) || false;
      };

      // ===================== APPLY SELECTED =====================
      for (let i of providerSelected || []) {
        for (let j of updatedData) {
          if (
            i?.start === j?.start &&
            i?.extendedProps?.provider_id === j?.extendedProps?.provider_id
          ) {
            j.status = 2;

            let date = new Date(j.start);
            date.setMinutes(date.getMinutes() + (maxTime || 0));
            j.end = date;
          }
        }
      }

      // ===================== HOLIDAY CREWS =====================
      const holidayCrewIds = new Set();

      if (availabilityMap && Object.keys(availabilityMap).length > 0) {
        Object.keys(availabilityMap).forEach((providerId) => {
          if (availabilityMap[providerId]?.includes(d1)) {
            holidayCrewIds.add(providerId);
          }
        });
      }

      setHolidayCrews([...holidayCrewIds]);

      // ===================== MAIN FILTER =====================
      let updated = [];

      for (let i of updatedData) {
        if (!i?.start || !i?.end) continue;

        const providerId = i?.extendedProps?.provider_id;
        const isHoliday = isCrewOnHoliday(providerId, i.start);

        i.extendedProps = i.extendedProps || {};
        i.extendedProps.isHoliday = isHoliday;

        if (isHoliday) {
          i.status = 99;
          i.extendedProps.isDisabled = true;
        }

        let flag = true;

        if ([2, 3].includes(i.status)) {
          updated.push(i);
          continue;
        }

        for (let j of providerSelected || []) {
          if (!j?.start || !j?.end) continue;

          let s1 = new Date(i.start);
          let e1 = new Date(i.end);
          let s2 = new Date(j.start);
          let e2 = new Date(j.end);

          let p1 = providerId;
          let p2 = j?.extendedProps?.provider_id;

          if (p1 === p2 && s1 >= s2 && e1 <= e2) {
            flag = false;
            break;
          }
        }

        // if (flag) {
        //   if (editable) {
        //     updated.push(i);
        //   } else if (timeSelect) {
        //     let [hours, mins] = timeSelect.split(":");
        //     let res = i.start.split("T")[1];

        //     if (res === `${hours}:${mins}:00`) {
        //       updated.push(i);
        //     }
        //   } else {
        //     updated.push(i); // fallback safe
        //   }
        // }
        if (flag) {
          updated.push(i);
        }
      }

      // ===================== SLOT ADJUSTMENT =====================
      for (let i of updated) {
        if (i.status === 2) {
          let date = new Date(i.start);
          let t1 = (maxTime || 0) / (crewCount || 1);

          date.setMinutes(date.getMinutes() + t1);
          i.end = convertWithoutTimseStamp(date);
        }
      }

      let u1 = cleanCrewSlots(updated || []);

      // ===================== EMPTY SAFE =====================
      if (!u1 || u1.length === 0) {
        setFilteredAppointments([]);
        setTimeRange({ startTime: "08:00:00", endTime: "20:00:00" });
        setShowCalender(true);
        setOpen(false);
        setLoading(false);
        return;
      }

      // ===================== TIME RANGE =====================
      const result = u1.reduce(
        (acc, item) => ({
          minStart: item.start < acc.minStart ? item.start : acc.minStart,
          maxEnd: item.end > acc.maxEnd ? item.end : acc.maxEnd
        }),
        {
          minStart: u1[0].start,
          maxEnd: u1[0].end
        }
      );

      let min1 = result?.minStart?.split("T")?.[1];
      let max1 = result?.maxEnd?.split("T")?.[1];
      if (!min1 || min1.includes("NaN")) {
        min1 = "08:00:00";
      }
      if (!max1 || max1.includes("NaN")) {
        max1 = "20:00:00";
      }
      setTimeRange({
        startTime: min1,
        endTime: max1
      });

      setFilteredAppointments(u1);
      setShowCalender(true);
      setOpen(false);
      setLoading(false);
    } catch (e) {
      console.log("err--", e);
      setLoading(false);
    }
  };

  const convertTo12Hour = (time24) => {
    if (!time24) return "";
    const [hourStr, minute] = time24?.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12; // Convert 0 to 12
    return `${hour}:${minute} ${ampm}`;
  };

  const onHideOrderConf = () => {
    setShowOrderConf(false);
    if (group_id) navigate("/dashboard");
    else navigate("/payments");
  };

  if (loading && isEditing) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner animation="border" variant="danger" />
      </div>
    );
  }

  const formatDateDisplay = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  function close() {
    setOpen(false);
  }

  // Generate Invoice (Bookings.jsx): same shared buildInvoiceHtml as
  // DashboardDetailed.jsx's multi-booking download, called here for just
  // this one booking. get_ezp_booking_by_id's custom_items comes as
  // "name::price||name2::price2" (GROUP_CONCAT), unlike DashboardDetailed's
  // comma-separated item_names/item_prices - normalized to the same
  // addedItems: [{name, price}] shape buildInvoiceHtml expects.
  const refreshInvoiceInfo = async () => {
    try {
      const res = await apicall.get(`get_ezp_booking_by_id?group_id=${group_id}`);
      setInvoiceInfo(toInvoiceInfo(res?.data?.[0]));
    } catch (e) {
      console.error("Could not refresh invoice info:", e);
    }
  };

  // Admin / vendor only (the API enforces the same roles).
  const canCancelInvoice = getPersonaForRole(apicall.getRole()) === PERSONA.ADMIN_VENDOR;

  const loadInvoiceImpacts = async () => {
    const res = await invoiceService.getInvoiceImpact({ invoiceId: invoiceInfo?.ref });
    const { invoice, bookings, has_payments } = res.data;
    return [
      {
        id: invoice.id,
        number: invoice.invoice_number,
        currency: invoice.currency || currency,
        hasPayments: has_payments,
        items: bookings.map((b) => ({
          key: String(b.group_id ?? `b${b.booking_id}`),
          label: b.display_booking_id || `Booking #${b.booking_id}`,
          detail: [b.customer_name, b.service_date].filter(Boolean).join(" - "),
          amount: b.total,
          paid: b.paid_amount,
        })),
      },
    ];
  };

  const cancelBookingInvoice = async (reason, impacts) => {
    const res = await invoiceService.cancelInvoice(impacts[0].id, reason);
    if (res?.status !== 200) throw new Error(res?.message || "Could not cancel the invoice");
    toast.success(`Invoice ${impacts[0].number} cancelled`);
    setShowCancelInvoice(false);
    await refreshInvoiceInfo();
  };

  // invoiceDate: 'YYYY-MM-DD' from the Generate Invoice popup.
  const generateInvoiceForBooking = async (invoiceDate) => {
    const booking = existingData?.[0];
    if (!booking) return;
    try {
      setLoading(true);
      const [vendorRes, partsRes, assetsByBookingId] = await Promise.all([
        apicall.get("get_ezp_vendor_data"),
        inventoryService.getBookingParts(booking.id),
        getInvoiceAssets([booking.id]),
      ]);
      const parts = Array.isArray(partsRes?.data) ? partsRes.data : [];

      const addedItems = (booking.custom_items || "")
        .split("||")
        .map((pair) => {
          const [name, price] = pair.split("::");
          return name ? { name: name.trim(), price } : null;
        })
        .filter(Boolean);

      // One invoice line per offering: existingData has a row per offering
      // (name + price); booking-level total/tax/discount/paid come from the
      // first row and are split across those lines by splitIntoOfferings.
      const { htmlContent, booking_ids } = buildInvoiceHtml({
        bookings: splitIntoOfferings(
          {
            booking_id: booking.id,
            group_id: group_id,
            display_booking_id: booking.display_booking_id,
            org_id: booking.org_id,
            service_name: booking.service_name,
            service_type_name: booking.service_type_name,
            service_offering_name: booking.service_offering_name,
            service_date: booking.service_date,
            service_time: booking.service_time,
            total: booking.total,
            tax: booking.tax,
            discount: booking.discount,
            total_paid_amount: booking.total_paid_amount,
            payment: booking.payment,
            commission: booking.commission,
            partner_name: booking.partner_name,
            user_name: booking.user_name,
            house_no: booking.house_no,
            area: booking.area,
            landmark: booking.landmark,
            location: booking.location,
            state: booking.state,
            phone_number: booking.phone_number,
            addedItems,
          },
          existingData
        ),
        partsByBookingId: { [booking.id]: parts },
        assetsByBookingId,
        vendorData: vendorRes?.data,
        currency,
        isClinicService: false,
        invoiceDate,
      });

      await apicall.generate_pdf({ booking_ids, htmlContent, currency, invoice_date: invoiceDate }, "generate_pdf");
      toast.success("Invoice generated");
      await refreshInvoiceInfo();
    } catch (err) {
      console.error("Generate invoice failed:", err);
      toast.error(invoiceService.invoiceErrorMessage(err, "Failed to generate invoice"));
    } finally {
      setLoading(false);
    }
  };

  // console.log(appointments, "appointments");
  // console.log(filteredAppointments, "filteredAppointments");

  return (
    <>
      <div onClick={close}>
        <div className="d-flex justify-content-between align-items-center">
          {group_id ? (
            <h4 className="mb-0">Edit {getBookingLabel(industry)} - {display_booking_id}</h4>
          ) : (
            <h4 className="mb-0">New {getBookingLabel(industry)}</h4>
          )}
          <div className="d-flex gap-2">
            {group_id && Number(existingData?.[0]?.status) === 4 && !invoiceInfo && (
              <Button
                type="button"
                variant="outline-danger"
                size="sm"
                className="btn btn-danger"
                disabled={loading}
                onClick={() => setShowInvoiceDate(true)}
              >
                Generate Invoice
              </Button>
            )}
            {group_id && invoiceInfo && (
              <>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  disabled={!invoiceInfo.url}
                  onClick={() => window.open(invoiceInfo.url, "_blank", "noopener")}
                >
                  View Invoice {invoiceInfo.number}
                </Button>
                {canCancelInvoice && (
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    disabled={loading}
                    onClick={() => setShowCancelInvoice(true)}
                  >
                    Cancel Invoice
                  </Button>
                )}
              </>
            )}
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              className="btn btn-danger"
              onClick={() => {
                window.open(`/book-service/vendor/${vendor_id}`, "_blank");
              }}
            >
              Booking Link
            </Button>
          </div>
        </div>

        <InvoiceCancelDialog
          show={showCancelInvoice}
          onHide={() => setShowCancelInvoice(false)}
          loadImpacts={loadInvoiceImpacts}
          selectedKeys={new Set([String(group_id)])}
          noun="booking"
          onConfirm={cancelBookingInvoice}
        />

        <InvoiceDateDialog
          show={showInvoiceDate}
          onHide={() => setShowInvoiceDate(false)}
          count={1}
          onConfirm={(invoiceDate) => generateInvoiceForBooking(invoiceDate)}
        />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            setValues,
            dirty
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <SubmitErrorReporter />
                <div className="mt-4 mb-2">
                  <Accordion
                    defaultActiveKey={["0", "1"]}
                    alwaysOpen
                  >
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>{isSportsService ? "Player Information" : "Customer Information"}</Accordion.Header>
                      <Accordion.Body>
                        <div className="mb-4">
                          {/* <h6 className="mb-4 section-heading">Personal Details</h6> */}
                          <Row className="mb-4" data-tour="booking-phone">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  Mobile Number
                                  <span style={{ color: "red" }}>*</span>
                                  <FieldHelpTooltip
                                    {...(isSportsService ? HELP_CONTENT.bookings.SportsphoneNumber : HELP_CONTENT.bookings.phoneNumber)}
                                  />
                                </Form.Label>
                                <PhoneInput
                                  country={currency === "INR" ? "in" : "ae"}
                                  // country={phoneCountryCode}
                                  onlyCountries={["ae", "in"]}
                                  value={values.phoneNumber}
                                  disabled={!editable}
                                  onChange={(value) => {
                                    const cleanedValue = value.replace(
                                      /\D/g,
                                      ""
                                    );
                                    setFieldValue("phoneNumber", cleanedValue);
                                    setFieldTouched("phoneNumber", true, false);

                                    // Only check for user existence if the number is valid
                                    if (cleanedValue.length >= 12) {
                                      debouncedCheckUserExists(
                                        "phoneNumber",
                                        cleanedValue,
                                        setFieldValue,
                                        setValues
                                      );
                                    }
                                  }}
                                  inputStyle={{
                                    width: "100%",
                                    height: "40px",
                                    backgroundColor: "white",
                                    borderColor:
                                      touched.phoneNumber && errors.phoneNumber
                                        ? "#dc3545"
                                        : "#E9ECEF"
                                  }}
                                  inputProps={{
                                    name: "phoneNumber"
                                    // required: true,
                                  }}
                                />
                                {touched.phoneNumber && errors.phoneNumber && (
                                  <div className="invalid-feedback d-block">
                                    {errors.phoneNumber}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  Origin {getBookingLabel(industry)} ID
                                  <FieldHelpTooltip
                                    {...HELP_CONTENT.bookings.originBookingId}
                                  />
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  name="org_id"
                                  placeholder={`Enter original ${getBookingLabel(industry).toLowerCase()} ID...`}
                                  value={values.org_id}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={!editable}
                                  isInvalid={touched.org_id && errors.org_id}
                                  className="form-input"
                                  style={{ height: "40px" }}
                                />
                                {touched.org_id && errors.org_id && (
                                  <div className="invalid-feedback d-block">
                                    {errors.org_id}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  {isSportsService ? "Player Name" : "Customer Name"}
                                  <span style={{ color: "red" }}>*</span>
                                  <FieldHelpTooltip
                                    {...(isSportsService ? HELP_CONTENT.bookings.SportscustomerName : HELP_CONTENT.bookings.customerName)}
                                  />
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  name="userName"
                                  placeholder={isSportsService ? "Enter Player Name..." : "Enter Customer Name..."}
                                  value={values.userName}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={!editable}
                                  isInvalid={
                                    touched.userName && errors.userName
                                  }
                                  className="form-input"
                                />
                                {touched.userName && errors.userName && (
                                  <Form.Control.Feedback type="invalid">
                                    {errors.userName}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  Email Address
                                  <FieldHelpTooltip
                                    {...HELP_CONTENT.bookings.email}
                                  />
                                </Form.Label>
                                <Form.Control
                                  type="email"
                                  name="email"
                                  placeholder="Enter email address"
                                  value={values.email}
                                  disabled={!editable}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  isInvalid={touched.email && errors.email}
                                  className="form-input"
                                />

                                {touched.email && errors.email && (
                                  <Form.Control.Feedback type="invalid">
                                    {errors.email}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                        <div className="mb-4">
                          <h6 className="section-heading">Full Address</h6>
                          <Row className="g-3" data-tour="booking-address">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  Address{" "}
                                  {!(isSportsService || isCarService || isSalonService) && (
                                    <span style={{ color: "red" }}>*</span>
                                  )}
                                  <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportsaddress : HELP_CONTENT.bookings.address)} />
                                </Form.Label>

                                  <div className="d-flex align-items-center gap-2">
                                    <div style={{ flex: 1 }}>
                                      {isLoaded && (
                                        <Autocomplete
                                          onLoad={onLoad}
                                          onPlaceChanged={() =>
                                            onPlaceChanged(setFieldValue)
                                          }
                                          options={{
                                            componentRestrictions: {
                                              country:
                                                currency === "INR" ? "in" : "ae"
                                            }
                                          }}
                                        >
                                          <Form.Control
                                            type="text"
                                            name="location"
                                            placeholder="Search address"
                                            value={values.location}
                                            onChange={handleChange}
                                            className="form-input"
                                          />
                                        </Autocomplete>
                                      )}
                                    </div>

                                    {values.location && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => {
                                          setFieldValue("location", "");
                                          setFieldValue("area", "");
                                          setFieldValue("state", "");
                                          setFieldValue("lat", "");
                                          setFieldValue("lng", "");
                                        }}
                                      >
                                        ✖
                                      </button>
                                    )}
                                  </div>

                                  {locationError && (
                                    <div className="invalid-feedback d-block">
                                      {locationError}
                                    </div>
                                  )}
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  House / Flat / Building No
                                  {!(isSportsService || isCarService || isSalonService) && (
                                    <span style={{ color: "red" }}>*</span>
                                  )}
                                  <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportshouseno : HELP_CONTENT.bookings.houseNo)} />
                                </Form.Label>

                                  <Form.Control
                                    type="text"
                                    name="house_no"
                                    placeholder="Enter house / flat / building no"
                                    value={values.house_no}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={!editable}
                                    isInvalid={
                                      touched.house_no && errors.house_no
                                    }
                                    className="form-input"
                                    style={{ height: "40px" }}
                                  />

                                {touched.house_no && errors.house_no && (
                                  <div className="invalid-feedback d-block">
                                    {errors.house_no}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  Area
                                  {!(isSportsService || isCarService || isSalonService) && (
                                    <span style={{ color: "red" }}>*</span>
                                  )}
                                  <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportsarea : HELP_CONTENT.bookings.area)} />
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  name="area"
                                  placeholder="Enter area details..."
                                  value={values.area}
                                  onChange={handleChange}
                                  disabled={!editable}
                                  onBlur={handleBlur}
                                  isInvalid={touched.area && errors.area}
                                  className="form-input"
                                  style={{ height: "40px" }}
                                />
                                {touched.area && errors.area && (
                                  <div className="invalid-feedback d-block">
                                    {errors.area}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  {currency === "INR" ? "State" : "Emirate"}
                                  {!(isSportsService || isCarService || isSalonService) && (
                                    <span
                                      style={{ color: "red", fontSize: "1rem" }}
                                    >
                                      *
                                    </span>
                                  )}
                                  <FieldHelpTooltip {...HELP_CONTENT.bookings.state} />
                                </Form.Label>
                                <Dropdown
                                  name="state"
                                  value={values.state}
                                  options={states?.map((s) => ({
                                    label: s.name,
                                    value: s.name,
                                  }))}
                                  placeholder={
                                    currency === "INR"
                                      ? "Select State"
                                      : "Select Emirate"
                                  }
                                  disabled={!editable}
                                  onChange={(e) => {
                                    // console.log("Selected Emirate:", e.value);

                                      handleStateChange(e, setFieldValue); // your existing function
                                      setFieldValue("state", e.value);
                                    }}
                                    onBlur={handleBlur}
                                    className={`w-100 ${
                                      touched.state && errors.state
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    style={{
                                      height: "40px",
                                      backgroundColor: "white",
                                      border:
                                        touched.state && errors.state
                                          ? "1px solid #dc3545"
                                          : "1px solid #E9ECEF"
                                    }}
                                  />

                                  {touched.state && errors.state && (
                                    <div className="invalid-feedback d-block">
                                      {errors.state}
                                    </div>
                                  )}
                                </Form.Group>
                              </Col>

                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label>
                                    Landmark
                                    <FieldHelpTooltip
                                      {...(isSportsService ? HELP_CONTENT.bookings.Sportslandmark : HELP_CONTENT.bookings.landmark)}
                                    />
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="landmark"
                                    placeholder="Enter landmark details..."
                                    value={values.landmark}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={!editable}
                                    isInvalid={
                                      touched.landmark && errors.landmark
                                    }
                                    className="form-input"
                                    style={{ height: "40px" }}
                                  />
                                  {touched.landmark && errors.landmark && (
                                    <div className="invalid-feedback d-block">
                                      {errors.landmark}
                                    </div>
                                  )}
                                </Form.Group>
                              </Col>

                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label>
                                    Country
                                    <FieldHelpTooltip
                                      {...(isSportsService ? HELP_CONTENT.bookings.Sportscountry : HELP_CONTENT.bookings.country)}
                                    />
                                  </Form.Label>
                                  <Dropdown
                                    name="country"
                                    value={currency === "INR" ? 2 : 1}
                                    options={countries?.map((c) => ({
                                      label: c.name,
                                      value: Number(c.country_id)
                                    }))}
                                    placeholder="Select Country"
                                    disabled={!editable}
                                    onChange={(e) => {
                                      const expectedCountryId =
                                        currency === "INR" ? 2 : 1;
                                      if (
                                        Number(e.value) !== expectedCountryId
                                      ) {
                                        toast.error(
                                          currency === "INR"
                                            ? "Please select a country related to your profile (India)."
                                            : "Please select a country related to your profile (UAE)."
                                        );
                                        return;
                                      }

                                      handleCountryChange(e, setFieldValue); // your existing function
                                      setFieldValue("country", e.value);
                                    }}
                                    onBlur={handleBlur}
                                    className={`w-100 ${
                                      touched.country && errors.country
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    style={{
                                      height: "40px",
                                      backgroundColor: "white",
                                      border:
                                        touched.country && errors.country
                                          ? "1px solid #dc3545"
                                          : "1px solid #E9ECEF"
                                    }}
                                  />
                                  {touched.country && errors.country && (
                                    <div className="invalid-feedback d-block">
                                      Please select a country
                                    </div>
                                  )}
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label>
                                    Longitude and Latitude
                                    <FieldHelpTooltip
                                      {...HELP_CONTENT.bookings.latLng}
                                    />
                                  </Form.Label>
                                  <Row className="g-2">
                                    <Col>
                                      <Form.Control
                                        type="text"
                                        name="lat"
                                        placeholder="Latitude"
                                        value={values.lat}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={!editable}
                                        isInvalid={touched.lat && errors.lat}
                                        className="form-input"
                                        style={{
                                          fontSize: "14px",
                                          height: "40px"
                                        }}
                                      />
                                      {touched.lat && errors.lat && (
                                        <div className="invalid-feedback d-block">
                                          {errors.lat}
                                        </div>
                                      )}
                                    </Col>
                                    <Col>
                                      <Form.Control
                                        type="text"
                                        name="lng"
                                        placeholder="Longitude"
                                        value={values.lng}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={!editable}
                                        isInvalid={touched.lng && errors.lng}
                                        className="form-input"
                                        style={{
                                          fontSize: "14px",
                                          height: "40px"
                                        }}
                                      />
                                      {touched.lng && errors.lng && (
                                        <div className="invalid-feedback d-block">
                                          {errors.lng}
                                        </div>
                                      )}
                                    </Col>
                                  </Row>
                                </Form.Group>
                              </Col>
                            </Row>
                          </div>
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="1">
                      <Accordion.Header>
                        {isSportsService
                          ? "Sports Information"
                          : "Service Information"}
                      </Accordion.Header>
                      <Accordion.Body>
                        <div className="service-selection-card mb-4">
                          <ServiceSpecificsSection
                            customerId={user_id || null}
                            bookingId={group_id ? existingData?.[0]?.id : null}
                            onChange={setServiceSpecifics}
                            onLoaded={handleServiceSpecificsLoaded}
                          />
                          <div className="mb-4" data-tour="booking-details">
                            <Col md={4} className="mb-2">
                              <Form.Group>
                                <Form.Label>
                                  Profile
                                  <FieldHelpTooltip
                                    {...(isSportsService ? HELP_CONTENT.bookings.Sportsprofile : HELP_CONTENT.bookings.profile)}
                                  />
                                </Form.Label>
                                <Form.Select
                                  name="partner"
                                  value={values.partner}
                                  onChange={(e) => {
                                    handleChange(e);
                                    setPartner_id(e.target.value);
                                    fetchServiceData(
                                      selectedService,
                                      e.target.value
                                    );
                                  }}
                                  onBlur={handleBlur}
                                  isInvalid={touched.partner && errors.partner}
                                  className={`form-input ${
                                    values.partner === ""
                                      ? "placeholder-style"
                                      : ""
                                  }`}
                                  style={{
                                    height: "40px",
                                    backgroundColor: "white",
                                    border:
                                      touched.partner && errors.partner
                                        ? "1px solid #dc3545"
                                        : "1px solid #E9ECEF"
                                  }}
                                >
                                  <option value="">Select Profile</option>
                                  {partners?.map((partner) => (
                                    <option
                                      key={partner?.id}
                                      value={partner?.id}
                                    >
                                      {partner?.partner_name}
                                    </option>
                                  ))}
                                </Form.Select>

                                {touched.partner && errors.partner && (
                                  <div className="invalid-feedback d-block">
                                    {errors.partner}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            <h6
                              className="section-title"
                              data-tour="booking-services"
                            >
                              {isSportsService ? "Sports" : "Service Category"}
                              <span style={{ color: "red", fontSize: "1rem" }}>
                                *
                              </span>
                              <FieldHelpTooltip
                                {...(isSportsService ? HELP_CONTENT.bookings.SportsserviceOfferings : HELP_CONTENT.bookings.serviceOfferings)}
                              />
                            </h6>

                            {services && services.length > 0 ? (
                              <div className="d-flex gap-3 flex-wrap">
                                {services.map((ele) => {
                                  // Same idea as the Sub Category pills below:
                                  // a category with an added offering stays
                                  // highlighted after switching to another one.
                                  const hasAdded = serviceData?.some(
                                    (d) =>
                                      d?.added &&
                                      String(d?.service_id) ===
                                        String(ele?.service_id)
                                  );
                                  return (
                                    <div
                                      key={ele.service_id}
                                      onClick={() => setSelectedService(ele)}
                                      className={`service-type-pill ${
                                        ele?.service_id ===
                                          selectedService?.service_id ||
                                        hasAdded
                                          ? "active"
                                          : ""
                                      }`}
                                    >
                                      {ele?.service_name}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center p-4 border rounded bg-light">
                                <h6 className="text-danger mb-2">
                                  No Services Available
                                </h6>
                                <p className="text-muted mb-0">
                                  {isSportsService
                                    ? "Please add Sports & Sports Type from Admin Setup to continue booking."
                                    : "Please add Service & Type from Admin Setup to continue booking."}
                                </p>
                              </div>
                            )}
                          </div>
                          {/* SERVICE TYPE */}
                          {!Object.keys(groupedData).includes("null") && (
                            <div className="mb-4">
                              <h6 className="section-title">
                                Sub Category
                                <span
                                  style={{ color: "red", fontSize: "1rem" }}
                                >
                                  *
                                </span>
                              </h6>

                              <div className="d-flex gap-3 flex-wrap">
                                {Object.keys(groupedData).map((ele) => {
                                  // The offerings grid below only ever renders
                                  // groupedData[activeType] — a booking with items
                                  // added under multiple sub categories only shows
                                  // one tab's offering at a time, with nothing to
                                  // tell the vendor another tab also has an added
                                  // item. Show the pill as selected (same style as
                                  // the active tab) whenever it has one, instead of
                                  // only the currently-clicked tab looking selected.
                                  const hasAdded = groupedData[ele]?.some(
                                    (d) => d?.added
                                  );
                                  return (
                                    <div
                                      key={ele}
                                      onClick={() => setActiveType(ele)}
                                      className={`service-type-pill ${
                                        ele === activeType || hasAdded
                                          ? "active"
                                          : ""
                                      }`}
                                    >
                                      {ele}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* SELECT OFFERING */}
                          {services?.length > 0 && (
                            <>
                              {serviceTypes && serviceTypes.length > 0 ? (
                                <div>
                                  <h6 className="section-title">
                                    {isSportsService
                                      ? "Sports Type"
                                      : "Service Type"}
                                    <span
                                      style={{ color: "red", fontSize: "1rem" }}
                                    >
                                      *
                                    </span>
                                  </h6>

                                  <div className="row g-3">
                                    {groupedData?.[activeType]?.map((data) => (
                                      <div className="col-md-3" key={data.id}>
                                        <div
                                          onClick={() => addOffering(data)}
                                          className="offering-row-card d-flex justify-content-between align-items-center"
                                        >
                                          <div>
                                            <div className="fw-semibold">
                                              {data?.service_offering_name}
                                            </div>
                                            <div className="price-text">
                                              {currency} {data?.price}
                                            </div>
                                          </div>

                                          <div
                                            className={`btn btn-sm rounded-3 ${
                                              data?.added
                                                ? "btn-danger"
                                                : "btn-outline-danger"
                                            }`}
                                          >
                                            {data?.added ? "Added" : "Add"}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center p-4 border rounded bg-light">
                                  <h6 className="text-danger mb-2">
                                    No Service Types Available
                                  </h6>
                                  <p className="text-muted mb-0">
                                    Please add Service Type under the selected
                                    Service to continue booking.
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* ================= SUBSCRIPTION CARD ================= */}
                        {userFlexiSubs.length > 0 && (
                          <div className="service-selection-card mb-4">
                            <h6 className="section-title">
                              Apply Subscription{" "}
                              <span
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "400",
                                  color: "#6B7280"
                                }}
                              >
                                (optional)
                              </span>
                            </h6>
                            <Form.Select
                              value={
                                selectedSubscription
                                  ? selectedSubscription.user_subscription_id
                                  : ""
                              }
                              onChange={(e) => {
                                const val = e.target.value;
                                if (!val) {
                                  setSelectedSubscription(null);
                                  pendingSubRef.current = null;
                                  // Reset service information: reload current service with no pre-selection
                                  fetchServiceData(selectedService);
                                } else {
                                  const sub = userFlexiSubs.find(
                                    (s) =>
                                      String(s.user_subscription_id) === val
                                  );
                                  if (sub) {
                                    setSelectedSubscription(sub);
                                    pendingSubRef.current = sub;
                                    const matchedService = services?.find(
                                      (s) =>
                                        String(s.service_id) ===
                                        String(sub.service_id)
                                    );
                                    if (matchedService) {
                                      if (
                                        String(matchedService.service_id) ===
                                        String(selectedService?.service_id)
                                      ) {
                                        // Same service already active — fetch directly to apply auto-selection
                                        fetchServiceData(matchedService);
                                      } else {
                                        // Different service — update selectedService, useEffect will call fetchServiceData
                                        setSelectedService(matchedService);
                                      }
                                    }
                                  }
                                }
                              }}
                              style={{ maxWidth: "420px", height: "40px" }}
                            >
                              <option value="">
                                — None (no subscription) —
                              </option>
                              {userFlexiSubs.map((sub) => (
                                <option
                                  key={sub.user_subscription_id}
                                  value={sub.user_subscription_id}
                                >
                                  {sub.plan_name} — {sub.service_name}
                                  {sub.service_offering_name
                                    ? ` / ${sub.service_offering_name}`
                                    : ""}
                                </option>
                              ))}
                            </Form.Select>

                            {selectedSubscription && (
                              <div
                                style={{
                                  marginTop: "12px",
                                  padding: "12px 16px",
                                  background: "#FDE8EF",
                                  borderRadius: "10px",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "20px",
                                  fontSize: "13px",
                                  color: "#374151"
                                }}
                              >
                                {[
                                  {
                                    label: "Plan",
                                    value: selectedSubscription.plan_name
                                  },
                                  {
                                    label: "Service",
                                    value: selectedSubscription.service_name
                                  },
                                  selectedSubscription.service_type_name && {
                                    label: "Sub Category",
                                    value:
                                      selectedSubscription.service_type_name
                                  },
                                  selectedSubscription.service_offering_name && {
                                    label: "Service Type",
                                    value:
                                      selectedSubscription.service_offering_name
                                  },
                                  {
                                    label: "Pricing",
                                    value: `${currency} ${Number(
                                      selectedSubscription.pricing || 0
                                    ).toFixed(2)}`
                                  },
                                  {
                                    label: "Sessions",
                                    value:
                                      selectedSubscription.number_of_sessions ===
                                      "Limited"
                                        ? `${selectedSubscription.total_sessions} sessions`
                                        : selectedSubscription.number_of_sessions ||
                                          "-"
                                  },
                                  {
                                    label: "Plan End",
                                    value: selectedSubscription.plan_end_date
                                      ? new Date(
                                          selectedSubscription.plan_end_date
                                        ).toLocaleDateString()
                                      : "-"
                                  }
                                ]
                                  .filter(Boolean)
                                  .map(({ label, value }) => (
                                    <div key={label}>
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          color: "#C0335A",
                                          fontSize: "11px",
                                          textTransform: "uppercase",
                                          marginBottom: "2px"
                                        }}
                                      >
                                        {label}
                                      </div>
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          color: "#111827"
                                        }}
                                      >
                                        {value}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* ================= CARD 2 ================= */}
                        {/* SERVICE INFORMATION */}
                        <div className="service-summary-card">
                          <div className="p-3">
                            {/* DATE + TIME + CREW + STATUS + PAYMENT */}
                            {/* Start Date */}
                            <Row data-tour="booking-crew">
                              <Col
                                md={3}
                                style={{ marginBottom: "1%" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="d-flex  flex-column">
                                  <Form.Label>
                                    Select Date
                                    <span
                                      style={{ color: "red", fontSize: "1rem" }}
                                    >
                                      *
                                    </span>
                                  </Form.Label>
                                  <div className="position-relative">
                                    <button
                                      type="button"
                                      className="btn d-flex align-items-center gap-2 px-3 position-relative"
                                      style={{
                                        height: "40px",
                                        fontSize: "14px",
                                        borderRadius: "6px",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #E3EBF8",
                                        color: "#000000",
                                        justifyContent: "space-between",
                                        width: "100%"
                                      }}
                                      disabled={!editable}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpen(!open);
                                      }}
                                    >
                                      {formatDateDisplay(dateSelect)}
                                      <FaRegCalendarAlt className="position-absolute end-0 mx-2" />
                                    </button>

                                    {open && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "100%",
                                          left: 0,
                                          zIndex: 9999,
                                          backgroundColor: "#fff",
                                          boxShadow:
                                            "0 6px 20px rgba(0,0,0,0.15)",
                                          borderRadius: "12px",
                                          padding: "12px",
                                          width: "300px"
                                        }}
                                      >
                                        {/* Legend Header */}
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: "10px",
                                            fontSize: "12px",
                                            padding: "6px 8px",
                                            background: "#f8f9fb",
                                            borderRadius: "8px"
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px"
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: "#d01c4c",
                                                fontSize: "14px"
                                              }}
                                            >
                                              ●
                                            </span>
                                            <span>
                                              Vendor on Leave / Holiday
                                            </span>
                                          </div>
                                        </div>

                                        {/* Calendar */}
                                        <DatePicker
                                          inline
                                          selected={dateSelect}
                                          onChange={(date) => {
                                            if (!date) return;
                                            setProviderSelected([]);
                                            setDateSelect(date);
                                            getWorkerAvailableSlots(date);
                                          }}
                                          dateFormat="dd-MM-yyyy"
                                          minDate={new Date()}
                                          excludeDates={vendorBlockedDates}
                                          dayClassName={(date) => {
                                            const isBlocked =
                                              vendorBlockedDates?.some(
                                                (d) =>
                                                  d.toDateString() ===
                                                  date.toDateString()
                                              );

                                            if (isBlocked) {
                                              return "blocked-date";
                                            }

                                            return undefined;
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Col>

                              {/* Selected Time */}
                              <Col md={3}>
                                <div className="d-flex  flex-column">
                                  <Form.Label>
                                    Selected Time
                                    <span
                                      style={{ color: "red", fontSize: "1rem" }}
                                    >
                                      *
                                    </span>
                                  </Form.Label>
                                  <div
                                    onClick={() => {
                                      getWorkerAvailableSlots(dateSelect);
                                    }}
                                    className="d-flex gap-2 rounded border p-2"
                                    style={{ height: "40px" }}
                                  >
                                    {convertTo12Hour(timeSelect) || ""}
                                  </div>
                                </div>
                              </Col>

                              <Col md={3}>
                                <div className="d-flex  flex-column">
                                  <Form.Label>
                                    {isSportsService
                                      ? "Sports"
                                      : isCarService  || isSalonService
                                        ? "Service Provider"
                                        : "Crew"}
                                    <span
                                      style={{ color: "red", fontSize: "1rem" }}
                                    >
                                      *
                                    </span>
                                  </Form.Label>
                                  <div
                                    onClick={() => {
                                      getWorkerAvailableSlots(dateSelect);
                                    }}
                                    className="d-flex gap-2 rounded border p-2"
                                    style={{
                                      height: "40px",
                                      overflowX: "auto",
                                      overflowY: "hidden",
                                      alignItems: "center",
                                      flexWrap: "nowrap"
                                    }}
                                  >
                                    {providerSelected?.map((data, idx) => (
                                      <div
                                        key={idx}
                                        className="flex-center p-2 btn-danger rounded-4"
                                        style={{
                                          maxWidth: "120px",
                                          height: "28px",
                                          padding: "0 10px",
                                          fontSize: "12px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          flexShrink: 0
                                        }}
                                        title={
                                          data?.extendedProps?.search || ""
                                        }
                                      >
                                        {data?.extendedProps?.search || ""}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </Col>

                              {/* Optional bookable resource - renders nothing when the vendor has none */}
                              {showResourceField && (
                                <ResourceField
                                  resources={activeResources}
                                  value={resourceIds}
                                  onChange={setResourceIds}
                                  label={resourceLabel}
                                  date={dateSelect}
                                  time={resourceTime}
                                  durationMin={resourceDurationMin}
                                  excludeGroupId={group_id}
                                  currentLabel={resourceCurrentName}
                                  disabled={!editable}
                                />
                              )}

                              {/* Who does each step - only for a service with a workflow */}
                              {isEditing && showRunWorkflow && (
                                <WorkflowRunPanel
                                  workflow={runWorkflow}
                                  onChanged={setRunWorkflow}
                                  disabled={!editable}
                                  assignments={workflowAssign}
                                  onAssign={(stepKey, userId) =>
                                    setWorkflowAssign((prev) => ({ ...prev, [stepKey]: userId }))
                                  }
                                  mainPeople={[
                                    ...new Set(
                                      (providerSelected || [])
                                        .map((p) => p?.title)
                                        .filter(Boolean)
                                    )
                                  ]}
                                  date={dateSelect}
                                  time={resourceTime}
                                  durationMin={resourceDurationMin}
                                  excludeGroupId={group_id}
                                  onStatusChanged={(statusId) => {
                                    // The workflow moved the booking's status: show it, and
                                    // treat it as the saved value so Update doesn't write the old one back.
                                    setFieldValue("status", statusId);
                                    if (originalSnapshotRef.current) {
                                      originalSnapshotRef.current.status = Number(statusId);
                                    }
                                  }}
                                />
                              )}
                              {formWorkflow && isServiceStepOptional(formWorkflow.steps) && (
                                <Col xs={12}>
                                  <div className="alert alert-info py-2" style={{ fontSize: "13px" }} data-testid="legacy-optional-hint">
                                    This service's provider is optional, but this classic form still needs a slot picked on the calendar.
                                    To book a time now and assign the provider later, use the new booking form (open this page without
                                    <code> ?legacy=1</code>).
                                  </div>
                                </Col>
                              )}
                              {formWorkflow && !(isEditing && showRunWorkflow) && (
                                <WorkflowPanel
                                  definition={formWorkflow}
                                  initialAssignments={workflowInitial}
                                  assignments={workflowAssign}
                                  onChange={setWorkflowAssign}
                                  resources={activeResources}
                                  resourceIds={resourceIds}
                                  resourceLabel={resourceLabel}
                                  mainPeople={[
                                    ...new Set(
                                      (providerSelected || [])
                                        .map((p) => p?.title)
                                        .filter(Boolean)
                                    )
                                  ]}
                                  date={dateSelect}
                                  time={resourceTime}
                                  durationMin={resourceDurationMin}
                                  excludeGroupId={group_id}
                                  disabled={!editable}
                                />
                              )}

                              {/* Status */}
                              <Col md={3}>
                                <div className="d-flex  flex-column">
                                  <Form.Label>
                                    Status
                                    <FieldHelpTooltip
                                      {...(isSportsService ? HELP_CONTENT.bookings.Sportsstatus : HELP_CONTENT.bookings.status)}
                                    />
                                  </Form.Label>
                                  <Dropdown
                                    name="status"
                                    value={values.status}
                                    options={bookingStatus?.map((s) => ({
                                      label: s.name,
                                      value: s.id
                                    }))}
                                    placeholder="Select Status"
                                    onChange={(e) =>
                                      setFieldValue("status", e.value)
                                    }
                                    onBlur={handleBlur}
                                    className={`w-100 ${
                                      touched.status && errors.status
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    style={{
                                      height: "40px",
                                      backgroundColor: "white",
                                      border:
                                        touched.status && errors.status
                                          ? "1px solid #dc3545"
                                          : "1px solid #E9ECEF"
                                    }}
                                  />
                                  {touched.status && errors.status && (
                                    <div className="invalid-feedback d-block">
                                      {errors.status}
                                    </div>
                                  )}
                                </div>
                              </Col>

                              {/* Payment - only meaningful for Edit Booking, which
                                  posts directly. A new booking's payment status is
                                  decided at Checkout instead, synced with the actual
                                  payment method chosen there. */}
                              {group_id && (
                              <Col md={3}>
                                <div className="d-flex  flex-column">
                                  <Form.Label>
                                    Payment
                                    <FieldHelpTooltip
                                      {...(isSportsService ? HELP_CONTENT.bookings.SportspaymentStatus : HELP_CONTENT.bookings.paymentStatus)}
                                    />
                                  </Form.Label>
                                  <Dropdown
                                    name="payment"
                                    value={values.payment}
                                    options={paymentStatus?.map((p) => ({
                                      label: p.name,
                                      value: p.id
                                    }))}
                                    placeholder="Select Payment"
                                    onChange={(e) => {
                                      setFieldValue("payment", e.value);
                                      const selectedPaymentName =
                                        paymentStatus?.find(
                                          (p) => p.id === e.value
                                        )?.name;

                                      const isPaymentDone = isPaidPaymentStatus(selectedPaymentName);

                                      const totalObj = priceSection?.find(
                                        (item) => item.name === "Total"
                                      );

                                      const totalAmount = Number(
                                        totalObj?.amount || 0
                                      );

                                      if (isPaymentDone) {
                                        setPaidAmount(totalAmount);
                                      } else {
                                        setPaidAmount(0);
                                        // setBalanceDue(totalAmount); //  full amount pending
                                      }
                                    }}
                                    onBlur={handleBlur}
                                    className={`w-100 ${
                                      touched.payment && errors.payment
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    style={{
                                      height: "40px",
                                      backgroundColor: "white",
                                      border:
                                        touched.payment && errors.payment
                                          ? "1px solid #dc3545"
                                          : "1px solid #E9ECEF"
                                    }}
                                  />

                                  {touched.payment && errors.payment && (
                                    <div className="invalid-feedback d-block">
                                      {errors.payment}
                                    </div>
                                  )}
                                </div>
                              </Col>
                              )}
                              <Col md={3}>
                                <div className="d-flex flex-column">
                                  <Form.Label>Total Hours Reserved</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={
                                      isDataReady ? totalReservedHours : ""
                                    }
                                    readOnly
                                    style={{
                                      height: "40px",
                                      background: "#f8f9fa"
                                    }}
                                  />
                                </div>
                              </Col>
                            </Row>

                            {/* ================= PRICE SUMMARY ================= */}
                            <div
                              className="border rounded"
                              style={{ marginTop: "10px" }}
                              data-tour="booking-pricing"
                            >
                              {/* Header */}
                              <div className="d-flex justify-content-between align-items-center fw-semibold px-3 py-2 bg-light border-bottom">
                                <div className="d-flex align-items-center gap-3">
                                  <span>Service</span>

                                  <button
                                    type="button"
                                    className="btn btn-danger btn-primary d-flex align-items-center gap-1"
                                    style={{
                                      fontSize: "13px",
                                      padding: "4px 10px"
                                    }}
                                    onClick={addCustomService}
                                  >
                                    + Add item
                                  </button>
                                </div>

                                <span>Price ({currency})</span>
                              </div>

                              {/* Service Rows */}
                              {priceSection?.map((data, idx) => {
                                // Rendered in their own box (StagedPartsSection /
                                // PartsUsedSection) instead - skip rather than
                                // filter first, since customNameChange/
                                // customPriceChange/deleteCustomItem all index
                                // into priceSection by this loop's raw idx.
                                if (data?.isPart) return null;
                                const isLabelField =
                                  data?.name?.toLowerCase().includes("tax") ||
                                  data?.name?.toLowerCase().includes("total");

                                return (
                                  <div
                                    key={data.id || data.name}
                                    className={`d-flex justify-content-between align-items-center px-3 py-2 border-bottom ${
                                      isLabelField ? "bg-light fw-semibold" : ""
                                    }`}
                                  >
                                    {data?.isCustom ? (
                                      <Form.Control
                                        type="text"
                                        placeholder="E.g. Tips,Material,Extra Work"
                                        value={data?.name}
                                        size="sm"
                                        style={{ width: "210px" }}
                                        onChange={(e) =>
                                          customNameChange(e.target.value, idx)
                                        }
                                      />
                                    ) : (
                                      <span>{data?.name}</span>
                                    )}

                                    {isLabelField && !data?.isCustom ? (
                                      <span className="text-muted">
                                        {currency} {data?.amount}
                                      </span>
                                    ) : (
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted">
                                          {currency}
                                        </span>

                                        <Form.Control
                                          type="number"
                                          value={data?.amount}
                                          size="sm"
                                          style={{ width: "100px" }}
                                          onChange={(e) => {
                                            if (data?.isCustom) {
                                              customPriceChange(
                                                e.target.value,
                                                idx
                                              );
                                            } else {
                                              priceChange(e.target.value, data);
                                            }
                                          }}
                                        />
                                        {data?.isCustom && (
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() =>
                                              deleteCustomItem(idx)
                                            }
                                          >
                                            ✕
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
                                <span>Paid Amount</span>
                                <div className="d-flex align-items-center gap-2">
                                  <span>{currency}</span>
                                  <Form.Control
                                    type="number"
                                    value={paidAmount}
                                    size="sm"
                                    style={{ width: "100px" }}
                                    onChange={(e) =>
                                      setPaidAmount(Number(e.target.value))
                                    }
                                  />
                                </div>
                              </div>

                              {/* Balance Due */}
                              <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
                                <span>Balance Due</span>
                                <span>
                                  {currency} {balanceDue > 0 ? balanceDue : 0}
                                </span>
                              </div>
                            </div>

                            {/* Inventory / Parts Used (Feature 4, revised). Edit
                                booking: PartsUsedSection saves parts immediately
                                (unchanged), and lifts its subtotal up via
                                onPartsChanged so it counts toward the price
                                summary/Balance Due above - the increase is only
                                persisted to the booking itself when Update is
                                clicked (see the "unchanged" short-circuit below).
                                New booking: StagedPartsSection stages parts
                                locally as priceSection rows (isPart) - nothing
                                is persisted until the booking is actually
                                created in Checkout.jsx. */}
                            {group_id ? (
                              <>
                                {pendingBalanceIncrease > 0 && (
                                  <Alert variant="warning" className="mt-3 mb-0">
                                    Adding parts increases this booking's balance due by {currency}{" "}
                                    {pendingBalanceIncrease.toFixed(2)}. Click Update to save the new total.
                                  </Alert>
                                )}
                                <PartsUsedSection
                                  bookingId={existingData?.[0]?.id}
                                  currency={currency}
                                  onPartsChanged={(subtotal) => {
                                    // The baseline comes from the dedicated
                                    // fetch in getDataByID, not from "was this
                                    // the first call" - that call-order race
                                    // could either wrongly baseline at 0 for a
                                    // booking that already had parts, or
                                    // wrongly treat a real load as "changed."
                                    // If the baseline fetch hasn't resolved
                                    // yet, don't flag a change - only compare
                                    // once it's known.
                                    if (
                                      originalPartsSubtotalRef.current !== null &&
                                      Number(subtotal) !== Number(originalPartsSubtotalRef.current)
                                    ) {
                                      partsChangedRef.current = true;
                                    }
                                    setEditPartsSubtotal(subtotal);
                                  }}
                                />
                              </>
                            ) : (
                              <StagedPartsSection
                                parts={priceSection.filter((p) => p?.isPart)}
                                currency={currency}
                                onAdd={addStagedPart}
                                onRemove={removeStagedPart}
                              />
                            )}

                            <div className="mt-3">
                              <label className="form-label fw-semibold">
                                Additional Notes
                                <FieldHelpTooltip
                                  {...(isSportsService ? HELP_CONTENT.bookings.Sportsnotes : HELP_CONTENT.bookings.notes)}
                                />
                              </label>

                              <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="Enter any additional notes or service instructions..."
                                value={additionalNotes}
                                onChange={(e) =>
                                  setAdditionalNotes(e.target.value)
                                }
                                style={{
                                  fontSize: "14px",
                                  resize: "none"
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
                {recentBookings.length > 0 && (
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      borderRadius: "10px",
                      border: "1px solid #dee2e6",
                      background: "#fff"
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        padding: "5px",
                        fontSize: "16px",
                        fontWeight: "600",
                        borderBottom: "1px solid #dee2e6",
                        background: "#f8f9fa"
                      }}
                    >
                      Recent Bookings
                    </div>

                    <Table
                      striped
                      bordered
                      hover
                      size="sm"
                      style={{ marginBottom: 0 }}
                    >
                      <thead
                        style={{ fontSize: "13px", background: "#f1f3f5" }}
                      >
                        <tr>
                          <th>Booking Id</th>
                          <th>{isSportsService ? "Player Name" : "Customer Name"}</th>
                          <th>
                            {isSportsService ? "Sports" : "Service Category"}
                          </th>
                          <th>
                            {isSportsService
                              ? "Sports Type"
                              : "Service Sub category"}
                          </th>
                          <th>
                            {isSportsService
                              ? "Sports Offering"
                              : "Service Type"}
                          </th>
                          <th>Service Time</th>
                          <th>Service Date</th>
                          <th>Amount</th>
                          <th>
                            {isSportsService
                              ? "Sports"
                              : isCarService  || isSalonService
                                ? "Service Provider"
                                : "Crew"}
                          </th>
                        </tr>
                      </thead>
                      <tbody style={{ fontSize: "13px" }}>
                        {recentBookings.map((item, index) => (
                          <tr key={index}>
                            <td>{item.display_booking_id}</td>
                            <td>{item.customer_name}</td>{" "}
                            {/* Use customer_name */}
                            <td>{item.service_name}</td>
                            <td>{item.service_type_name}</td>
                            <td>{item.service_offering}</td>
                            <td>{item.service_time}</td>
                            <td>
                              {new Date(item.service_date).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric"
                                }
                              )}
                            </td>
                            <td>{Number(item.total).toFixed(2)}</td>
                            <td>
                              {formatStepAssignments(item) ||
                              realProviderNames(item)
                                .split(",")
                                .filter(Boolean)
                                .map((name, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      background: "#F1F5F9",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      marginRight: "4px",
                                      display: "inline-block"
                                    }}
                                  >
                                    {name.trim()}
                                  </span>
                                ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                <div className="d-flex justify-content-center mb-0 gap-4 flex-wrap">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="custom-btn btn-large"
                    style={{ marginTop: "10px", minWidth: 160 }}
                    onClick={() => {
                      if (group_id) {
                        navigate(-1);
                        return;
                      }
                      const hasInput =
                        dirty ||
                        providerSelected.length > 0 ||
                        additionalNotes !== "" ||
                        serviceData.some((s) => s.added);
                      if (hasInput) {
                        setShowCancelConfirm(true);
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="danger"
                    disabled={isSubmitting}
                    className="custom-btn btn-large"
                    style={{ marginTop: "10px", minWidth: 160 }}
                  >
                    {group_id ? "Update" : "Checkout →"}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>

        <Modal className="flex-center calendarMessage" show={!!calenderMessage}>
          <div onClick={() => setCalenderMessage("")} className="closeCalender">
            X
          </div>
          <div style={{ padding: "1rem" }}>{calenderMessage}</div>
        </Modal>

        <Modal
          show={showCalender || !!calenderMessage}
          onHide={onHide}
          centered
          size="xl"
          backdrop={true}
          keyboard={true}
          // backdrop="static"
          dialogClassName="custom-appointment-modal"
          contentClassName="rounded-4 border-0"
        >
          <Modal.Header closeButton className="border-0 pt-4 px-4">
            <Modal.Title className="w-100 fs-5 fw-bold">
              {" "}
              {isSportsService
                ? "Assign Sports"
                : isCarService  || isSalonService
                  ? "Assign Service Provider"
                  : "Assign Crew"}
            </Modal.Title>
          </Modal.Header>

          <hr className="mx-4 my-0" />

          <Modal.Body className="px-4 py-4 removeHeader bookingCalender ">
            {initialAppointments.length === 0 ? (
              <div className="flexCenter">
                <Link to="/providers/add">
                  {isSportsService
                    ? "Add sports member for this service"
                    : isCarService  || isSalonService
                      ? "Add service provider for this service"
                      : "Add crew for this service"}
                </Link>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                  {providerSelected?.length > 0 && (
                    <div
                      style={{
                        background: "#F8FAFF",
                        border: "1px solid #E3EBF8",
                        borderLeft: "3px solid #c9194a",
                        borderRadius: "8px",
                        padding: "12px 16px",
                        fontSize: "13px",
                        color: "#334155",
                        flex: "1",
                        minWidth: 0
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "700",
                          fontSize: "11px",
                          color: "#c9194a",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          marginBottom: "10px"
                        }}
                      >
                        Crew Summary
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px"
                        }}
                      >
                        {[
                          { label: "Crew Selected", value: crewCount },
                          {
                            label: "Service Duration",
                            value: `${serviceDuration} hrs`
                          },
                          {
                            label: "Duration Per Cleaner",
                            value: `${durationPerCleaner} mins`
                          }
                        ].map(({ label, value }) => (
                          <div
                            key={label}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}
                          >
                            <span
                              style={{ color: "#64748b", fontWeight: "500" }}
                            >
                              {label}
                            </span>
                            <span
                              style={{ fontWeight: "700", color: "#1e293b" }}
                            >
                              {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      background: "#F8FAFF",
                      border: "1px solid #E3EBF8",
                      borderLeft: "3px solid #037FE8",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      fontSize: "13px",
                      color: "#334155",
                      flex: "1",
                      minWidth: 0
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: "700",
                        fontSize: "11px",
                        color: "#037FE8",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: "10px"
                      }}
                    >
                      <FaInfoCircle size={12} />
                      How to use
                    </div>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px"
                      }}
                    >
                      {[
                        "Tap an available slot to assign it to the crew.",
                        "Tap a selected slot again to unassign it.",
                        "Red slots are already blocked and unavailable.",
                        "If all slots for a crew are unavailable, the crew is on leave for that day.",
                        "Click Submit when done."
                      ].map((text, i) => (
                        <li
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            lineHeight: "1.5"
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: "#037FE8",
                              flexShrink: 0,
                              marginTop: "6px"
                            }}
                          />
                          {text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {poolProfessionalIds.length > 0 && (
                  <div className="d-flex align-items-center justify-content-between mb-2" style={{ fontSize: "13px" }}>
                    <span className="text-muted">
                      {poolFilterActive
                        ? `Showing the crew mapped to ${chosenResource?.name}.`
                        : "Showing everyone."}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowAllCrew((v) => !v)}
                    >
                      {poolFilterActive ? "Show everyone" : `Only ${chosenResource?.name}'s crew`}
                    </button>
                  </div>
                )}

                <NewBookingCalendar
                  events={appointments}
                  date={dateSelect}
                  datesSet={datesSet}
                  editable={editable}
                  onAppointmentClick={handleAppointmentClick}
                  startTime={timeRange?.startTime}
                  endTime={timeRange?.endTime}
                  scrollToEventId="event-1"
                  headerToolbar={editable}
                  closeCalendarFUN={onHide}
                  submitEnabled={providerSelected?.length > 0}
                  onSubmit={onHide}
                />
              </>
            )}
          </Modal.Body>
        </Modal>

        <Modal
          show={showOrderConf}
          onHide={onHideOrderConf}
          centered
          size="xl"
          backdrop="static"
          dialogClassName="custom-appointment-modal addServiceModal"
          contentClassName="rounded-4 border-0"
        >
          <h4 className="text-center text-danger p-2 pb-0 ">
            {group_id ? `Order Updated` : `Order Confirmation`}
          </h4>

          <hr className="mx-4 my-0" />

          <Modal.Body className="px-4 py-4">
            <div className="d-flex flex-column gap-2">
              {/* Rows with no value are dropped so empty labels don't show. */}
              {[
                { label: "Order ID", value: modelData?.booking_id },
                {
                  label: "Selected Service",
                  value: [...new Set(modelData?.serviceData?.filter((ele) => ele.added)?.map((ele) => ele?.service_name).filter(Boolean))].join(", "),
                },
                {
                  label: "Selected Service Subcategory",
                  value: [
                    ...new Set(
                      modelData?.serviceData
                        ?.filter((ele) => ele.added && ele?.service_type_name && ele.service_type_name !== "null")
                        ?.map((ele) => ele?.service_type_name)
                    ),
                  ].join(", "),
                },
                {
                  label: "Selected Type",
                  value: modelData?.serviceData?.filter((ele) => ele.added)?.map((ele) => ele?.service_offering_name).filter(Boolean).join(", "),
                },
                { label: "Service Location", value: modelData?.state },
                {
                  label: "Scheduled Date & Time",
                  value: modelData?.date ? `${modelData.date}${modelData?.time ? ` @ ${convertTo12Hour(modelData.time)}` : ""}` : "",
                },
                modelData?.lat && modelData?.lng && {
                  label: "Google Map Link",
                  value: (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`https://www.google.com/maps/search/?api=1&query=${modelData?.lat},${modelData?.lng}`}
                    >
                      {`https://www.google.com/maps/search/?api=1&query=${modelData?.lat},${modelData?.lng}`}
                    </a>
                  ),
                },
                {
                  label: "Address",
                  value: [modelData?.house_no, modelData?.area, modelData?.location, modelData?.landmark, modelData?.state]
                    .filter((part) => part && String(part).trim() && part !== "null")
                    .join(", "),
                },
                { label: isSportsService ? "Player Name" : "Customer Name", value: modelData?.userName },
                {
                  label: isSportsService ? "Player Contact" : "Customer Contact",
                  value: modelData?.phoneNumber ? formatPhoneNumber(modelData.phoneNumber, currency) : "",
                },
              ]
                .filter((row) => row && row.value !== null && row.value !== undefined && String(row.value).trim() !== "")
                .map((row) => (
                  <div key={row.label} className="d-flex align-items-start">
                    <div className="label" style={{ width: "180px", flexShrink: 0 }}>
                      {row.label}
                    </div>
                    <div style={{ width: "10px" }}>:</div>
                    <div style={{ flex: 1, wordBreak: "break-word" }}>{row.value}</div>
                  </div>
                ))}
            </div>
          </Modal.Body>
          <div className="d-flex justify-content-center align-items-center mb-2">
            <button
              className="btn btn-danger rounded "
              onClick={onHideOrderConf}
            >
              Close
            </button>
          </div>
        </Modal>

        <Modal
          show={showCancelConfirm}
          onHide={() => setShowCancelConfirm(false)}
          centered
          size="sm"
          backdrop="static"
          contentClassName="rounded-4 border-0"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fs-6 fw-bold">Discard changes?</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-2 pb-3">
            <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>
              You have unsaved input. Leaving now will discard all changes.
            </p>
          </Modal.Body>
          <Modal.Footer className="justify-content-center border-0 pt-0">
            <Button
              variant="outline-secondary"
              onClick={() => setShowCancelConfirm(false)}
              style={{ minWidth: 100 }}
            >
              Stay
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setShowCancelConfirm(false);
                navigate(-1);
              }}
              style={{ minWidth: 100 }}
            >
              Discard &amp; Leave
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default Bookings;
